import { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { HostawayListingMapModal } from '../components/admin/HostawayListingMapModal';
import { AccountAdoptionPanel } from '../components/admin/AccountAdoptionPanel';
import { XeroQuotaMiniBadge } from '../components/admin/XeroQuotaPanel';
import { XeroFinanceScopeReconsentBanner, shouldShowXeroFinanceScopeReconsentBanner } from '../components/admin/XeroFinanceScopeReconsentBanner';
import { BankConsentsPanel } from '../components/admin/BankConsentsPanel';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Check,
  CheckCircle,
  X,
  ExternalLink,
  Loader2,
  Settings2,
  RefreshCw,
  AlertCircle,
  Building2,
  Mail,
  FileSpreadsheet,
  MessageSquare,
  Cloud,
  Download,
  Clock,
  TrendingUp,
  Play,
  Calendar,
  Zap,
  Search,
  Inbox,
  Home,
  Key,
  Copy,
  Trash2,
  Plus,
  BookOpen,
  ChevronDown,
  ChevronRight,
  Shield,
  FolderTree,
  Link2,
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { getErrorMessage, isGoogleReauthRequiredError, getGoogleReauthMessage, isGoogleApiNotConfiguredError, getGoogleApiNotConfiguredMessage, describeGoogleScanError, describeGoogleScopesSummary } from '../lib/errors';
import { RevolutScheduledPayments } from '../components/RevolutScheduledPayments';
import { pennylaneAPI, schedulerAPI, googleAPI, wiseAPI, revolutAPI, xeroAPI, uplistingAPI, smoobuAPI, hostawayAPI, brosAPI, apiKeysAPI, apiDocsAPI, portfolioPropertiesAPI, bridgeAPI, openbankingAPI, enablebankingAPI, gocardlessAPI, diagnosticsAPI } from '../lib/api';
import { classifyObConnectError } from '../lib/openBankingErrors';
import { useEntityStore } from '../store';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, LabelList,
} from 'recharts';
import { formatCompact } from '../lib/chartFormatters';

// Integration categories and their integrations
// Must match backend GOCARDLESS_DEFAULT_INSTITUTION_ID (config.py).
const GC_DEFAULT_INSTITUTION_ID = 'BANQUE_BCP_BPSMFRPPXXX';

const getIntegrationCategories = (pennylaneStatus, googleAccounts = [], wiseStatus = null, revolutStatus = null, xeroStatus = null, uplistingStatus = null, brosStatus = null, smoobuStatus = null, hostawayStatus = null, scraperStatus = null, pennylaneEntityStatus = null, bridgeStatus = null, obStatus = null, ebStatus = null, gcStatus = null) => ({
  accounting: {
    label: 'Accounting',
    icon: FileSpreadsheet,
    description: 'Connect your accounting software for automatic sync',
    integrations: [
      {
        id: 'pennylane',
        name: 'Pennylane',
        logo: 'https://app.pennylane.com/img/logo-pennylane.svg',
        description: pennylaneEntityStatus?.connected
          ? `${pennylaneEntityStatus.connected_count}/${pennylaneEntityStatus.total_count} entity/ies connected`
          : 'French accounting (multi-entity)',
        authType: 'pennylane_api_key',
        status: (pennylaneEntityStatus?.connected || pennylaneStatus?.connected) ? 'connected' : 'disconnected',
        entities: (pennylaneEntityStatus?.entities || []).map(e => e.canonical_name),
        isReal: true,
        companyName: '',
        pennylaneEntityStatus,
      },
      {
        id: 'xero',
        name: 'Xero',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/95/Xero_software_logo.svg/512px-Xero_software_logo.svg.png',
        description: xeroStatus?.connected
          ? `${xeroStatus.tenants?.length || 0} tenant(s) connected`
          : 'UK accounting for BR Ltd, BI Ltd, BI UK, ECI & BH',
        authType: 'xero_oauth',
        status: xeroStatus?.connected ? 'connected' : 'disconnected',
        entities: ['BR Ltd', 'BI Ltd', 'BI UK', 'ECI', 'BH'],
        isReal: true,
        xeroStatus,
      },
    ],
  },
  banking: {
    label: 'Banking',
    icon: Building2,
    description: 'Connect your bank accounts for automatic transaction sync',
    integrations: [
      {
        id: 'revolut',
        name: 'Revolut Business',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/1e/Revolut.svg/512px-Revolut.svg.png',
        description: revolutStatus?.connected
          ? `${revolutStatus.connected_count}/${revolutStatus.total_count} account(s) connected`
          : 'Sync transactions from Revolut Business accounts',
        authType: 'revolut_oauth',
        status: revolutStatus?.connected ? 'connected' : 'disconnected',
        entities: ['BI Ltd', 'BI SAS', 'ECI', 'BI UK', 'BH'],
        isReal: true,
        revolutStatus,
      },
      {
        id: 'wise',
        name: 'Wise',
        logo: 'https://customer-assets.emergentagent.com/job_0cebf1af-b1f0-4a50-a6b0-068588b8af4f/artifacts/q8itkirq_image.png',
        description: wiseStatus?.connected
          ? `${wiseStatus.profiles_count} profile(s), ${wiseStatus.balances?.length || 0} balance(s)`
          : 'Multi-currency transfers and balance sync',
        authType: 'api_key',
        status: wiseStatus?.connected ? 'connected' : 'disconnected',
        entities: ['BR Ltd', 'Personal'],
        isReal: true,
        wiseStatus,
      },
      {
        id: 'bridge',
        name: 'Banque BCP (Bridge)',
        logo: 'https://customer-assets.emergentagent.com/job_0cebf1af-b1f0-4a50-a6b0-068588b8af4f/artifacts/jjgrsqso_image.png',
        description: bridgeStatus?.connected
          ? `${bridgeStatus.connected_count} connection(s), ${bridgeStatus.connections?.reduce((sum, c) => sum + (c.account_count || 0), 0) || 0} account(s)`
          : 'Banque BCP via Bridge API (scope-gated)',
        authType: 'bridge_connect',
        status: bridgeStatus?.connected ? 'connected' : 'disconnected',
        entities: ['Personal'],
        isReal: true,
        bridgeStatus,
      },
      {
        id: 'openbanking',
        name: 'Open Banking (TrueLayer)',
        logo: '',
        description: obStatus?.connected
          ? `${obStatus.connected_count} connection(s), ${obStatus.connections?.reduce((sum, c) => sum + (c.account_count || 0), 0) || 0} account(s)`
          : obStatus?.is_sandbox ? 'Mock Bank (Sandbox) via TrueLayer' : 'RBS, BCP, Revolut personal via TrueLayer',
        authType: 'ob_connect',
        status: obStatus?.connected ? 'connected' : 'disconnected',
        entities: ['BR Ltd', 'Personal'],
        isReal: true,
        obStatus,
      },
      {
        id: 'enablebanking',
        name: 'Enable Banking (EU PSD2)',
        logo: '',
        description: ebStatus?.sessions?.some(s => s.connected)
          ? `${ebStatus.sessions.filter(s => s.connected).length} session(s), ${ebStatus.sessions.reduce((sum, s) => sum + (s.account_count || 0), 0)} account(s)`
          : 'BNP Paribas FR via PSD2 (Enable Banking)',
        authType: 'eb_connect',
        status: ebStatus?.sessions?.some(s => s.connected) ? 'connected' : 'disconnected',
        entities: ['BI SAS', 'SCI 113'],
        isReal: true,
        ebStatus,
      },
      {
        id: 'gocardless',
        name: 'GoCardless (Bank Account Data)',
        logo: '',
        description: gcStatus?.connections?.some(c => c.status === 'linked')
          ? `${gcStatus.connections.filter(c => c.status === 'linked').length} connection(s), ${gcStatus.connections.reduce((sum, c) => sum + (c.account_count || 0), 0)} account(s)`
          : 'Banque BCP and other EU banks via GoCardless AIS',
        authType: 'gc_connect',
        status: gcStatus?.connections?.some(c => c.status === 'linked') ? 'connected' : 'disconnected',
        entities: ['BI SAS'],
        isReal: true,
        gcStatus,
      },
    ],
  },
  email: {
    label: 'Email & Drive',
    icon: Mail,
    description: 'Connect Gmail and Google Drive for invoice capture and storage',
    integrations: googleAccounts.map(acc => ({
      id: `google_${acc.id}`,
      accountId: acc.id,
      name: `Gmail & Drive`,
      email: acc.email,
      logo: 'https://customer-assets.emergentagent.com/job_0cebf1af-b1f0-4a50-a6b0-068588b8af4f/artifacts/dknjnzb3_image.png',
      description: acc.email,
      authType: 'google_oauth',
      status: acc.connected ? 'connected' : 'disconnected',
      entities: ['All'],
      isReal: true,
      gmail_enabled: acc.gmail_enabled,
      drive_enabled: acc.drive_enabled,
      last_sync: acc.last_sync,
      last_scan_at: acc.last_scan_at,
      last_scan_error: acc.last_scan_error,
      granted_scopes: acc.granted_scopes,
      missing_scan_scopes: acc.missing_scan_scopes,
      scopes_known: acc.scopes_known,
    })),
  },
  channel_manager: {
    label: 'Channel Manager',
    icon: Home,
    description: 'Short-term rental property management and booking channels',
    integrations: [
      {
        id: 'uplisting',
        name: 'Uplisting',
        logo: 'https://uploads-ssl.webflow.com/5f3108520188e7588ef6b3b7/5f3108520188e717eef6b403_favicon-32.png',
        description: uplistingStatus?.connected
          ? `${uplistingStatus.properties_count || 0} properties, ${uplistingStatus.reservations_count || 0} reservations`
          : 'Channel manager for Airbnb, Booking.com, Vrbo & direct bookings',
        authType: 'api_key',
        status: uplistingStatus?.connected ? 'connected' : 'disconnected',
        entities: ['AFPV Invest', 'Bonjour Immobilier', 'SCI 113', 'PFV Holding', 'BI SAS'],
        isReal: true,
        uplistingStatus,
      },
      {
        id: 'bros',
        name: 'BR OS',
        logo: null,
        description: brosStatus?.connected
          ? `${brosStatus.properties_count || 0} properties, ${brosStatus.bookings_count || 0} bookings`
          : 'Bonjour Residences internal operating system',
        authType: 'api_key',
        status: brosStatus?.connected ? 'connected' : 'disconnected',
        entities: ['Bonjour Residences'],
        isReal: true,
        brosStatus,
      },
      {
        id: 'smoobu',
        name: 'Smoobu',
        logo: null,
        description: smoobuStatus?.connected
          ? `${smoobuStatus.properties_count || 0} properties, ${smoobuStatus.reservations_count || 0} reservations`
          : 'Channel manager for ECI, BI UK & BI Ltd properties',
        authType: 'api_key',
        status: smoobuStatus?.connected ? 'connected' : 'disconnected',
        entities: ['ECI', 'BI UK', 'BI Ltd'],
        isReal: true,
        smoobuStatus,
      },
      {
        id: 'hostaway',
        name: 'Hostaway',
        logo: null,
        description: hostawayStatus?.connected
          ? `${hostawayStatus.properties_count || 0} properties, ${hostawayStatus.reservations_count || 0} reservations`
          : 'Primary channel manager for UK properties (ECI, BI UK, BI Ltd)',
        authType: 'api_key',
        status: hostawayStatus?.connected ? 'connected' : 'disconnected',
        entities: ['ECI', 'BI UK', 'BI Ltd'],
        isReal: true,
        hostawayStatus,
      },
      {
        id: 'smoobu_scraper',
        name: 'Smoobu (CWC - Browser)',
        logo: null,
        description: scraperStatus?.session_valid
          ? `Session active · Last scrape: ${scraperStatus.last_scrape_result || 'never'}`
          : scraperStatus?.has_session
            ? 'Session expired — re-import cookies'
            : 'Browser scraper for Cozy With Character properties',
        authType: 'cookie_import',
        status: scraperStatus?.session_valid ? 'connected' : 'disconnected',
        entities: ['CWC'],
        isReal: true,
        scraperStatus,
      },
    ],
  },
  notifications: {
    label: 'Notifications',
    icon: MessageSquare,
    description: 'Connect messaging apps for alerts',
    integrations: [
      {
        id: 'slack',
        name: 'Slack',
        logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d5/Slack_icon_2019.svg/512px-Slack_icon_2019.svg.png',
        description: 'Receive alerts in Slack channels',
        authType: 'oauth',
        status: 'disconnected',
        entities: ['All'],
      },
    ],
  },
  storage: {
    label: 'Storage',
    icon: Cloud,
    description: 'Cloud storage is included with Google Drive integration',
    integrations: [],
  },
});

// Google Account Card Component
export function GoogleAccountCard({ account, onConnect, onDisconnect, onScanInvoices }) {
  const [isConnecting, setIsConnecting] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const isConnected = account.status === 'connected';
  
  const handleConnect = async () => {
    setIsConnecting(true);
    try {
      await onConnect(account);
    } finally {
      setIsConnecting(false);
    }
  };
  
  const handleScan = async () => {
    setIsScanning(true);
    try {
      await onScanInvoices(account.accountId);
    } finally {
      setIsScanning(false);
    }
  };
  
  return (
    <Card 
      className={`transition-all hover:shadow-md ${isConnected ? 'border-green-500/30 bg-green-500/5' : ''}`}
      data-testid={`google-card-${account.accountId}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
            <img 
              src={account.logo} 
              alt="Gmail" 
              className="w-8 h-8 object-contain"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold truncate">{account.name}</h3>
              <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/30">
                LIVE
              </Badge>
              {isConnected ? (
                <Badge variant="success" className="bg-green-500/10 text-green-600 border-green-500/30">
                  <Check className="w-3 h-3 mr-1" /> Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-muted-foreground">
                  <X className="w-3 h-3 mr-1" /> Disconnected
                </Badge>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground mb-2">
              {account.email}
            </p>
            
            {isConnected && (
              <div className="flex flex-wrap gap-1 mb-3">
                {account.gmail_enabled && (
                  <Badge variant="outline" className="text-xs">
                    <Inbox className="w-3 h-3 mr-1" /> Gmail
                  </Badge>
                )}
                {account.drive_enabled && (
                  <Badge variant="outline" className="text-xs">
                    <Cloud className="w-3 h-3 mr-1" /> Drive
                  </Badge>
                )}
              </div>
            )}

            {isConnected && (
              <p className="text-xs text-muted-foreground mb-1" data-testid={`scopes-summary-${account.accountId}`}>
                {describeGoogleScopesSummary(account)}
              </p>
            )}

            {isConnected && (
              account.last_scan_error ? (() => {
                const desc = describeGoogleScanError(account.last_scan_error, account.missing_scan_scopes);
                return (
                  <div
                    className="flex items-start gap-1.5 text-xs text-destructive mb-2"
                    data-testid={`scan-error-${account.accountId}`}
                  >
                    <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="font-medium">{desc.title}.</span> {desc.detail}
                      {desc.reconnectHelps === false && (
                        <span className="font-medium"> Reconnect will not fix this.</span>
                      )}
                      {account.last_scan_at && ` Last attempt: ${new Date(account.last_scan_at).toLocaleString()}.`}
                    </span>
                  </div>
                );
              })() : account.last_scan_at ? (
                <p className="text-xs text-muted-foreground mb-2" data-testid={`last-scan-${account.accountId}`}>
                  Last scanned: {new Date(account.last_scan_at).toLocaleString()}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mb-2" data-testid={`last-scan-${account.accountId}`}>
                  Never scanned
                </p>
              )
            )}

            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  <Button
                    size="sm"
                    onClick={handleScan}
                    disabled={isScanning}
                    data-testid={`scan-${account.accountId}`}
                  >
                    {isScanning ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Scanning...
                      </>
                    ) : (
                      <>
                        <Search className="w-3 h-3 mr-1" /> Scan Invoices
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleConnect}
                    disabled={isConnecting}
                    data-testid={`reconnect-google-${account.accountId}`}
                  >
                    {isConnecting ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Reconnecting...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-3 h-3 mr-1" /> Reconnect
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDisconnect(account)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`disconnect-google-${account.accountId}`}
                  >
                    Disconnect
                  </Button>
                </>
              ) : (
                <Button 
                  size="sm"
                  onClick={handleConnect}
                  disabled={isConnecting}
                  data-testid={`connect-google-${account.accountId}`}
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    'Connect with Google'
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Drive Destination Settings Card
// Surfaces + lets Fan set the Google Drive destination root that
// GoogleDriveService files invoices under (root / jurisdiction / entity /
// year / month / Invoices / Inbound|Outbound). Previously a hardcoded
// fallback in config.py that was never visible or editable anywhere.
function DriveDestinationSettingsCard() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [rootFolderId, setRootFolderId] = useState('');
  const [driveId, setDriveId] = useState('');
  const [saving, setSaving] = useState(false);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.driveSettingsGet();
      setSettings(data);
    } catch (err) {
      toast.error(`Could not load Drive destination settings: ${getErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const startEditing = () => {
    setRootFolderId(settings?.root_folder_id || '');
    setDriveId(settings?.drive_id || '');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!rootFolderId.trim()) {
      toast.error('Root folder ID is required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await diagnosticsAPI.driveSettingsUpdate(rootFolderId.trim(), driveId.trim());
      setSettings(data);
      setEditing(false);
      toast.success('Drive destination root saved');
    } catch (err) {
      toast.error(`Could not save destination: ${getErrorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const configured = settings?.configured;

  return (
    <Card className="mb-4" data-testid="drive-destination-settings-card">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <FolderTree className="w-4 h-4" />
          Drive Destination Root
        </CardTitle>
        <CardDescription>
          Where invoice PDFs are filed: root / jurisdiction / entity / year / month / Invoices / Inbound-Outbound
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...
          </div>
        ) : editing ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor="drive-root-folder-id" className="text-xs">Root folder ID (required)</Label>
              <Input
                id="drive-root-folder-id"
                value={rootFolderId}
                onChange={(e) => setRootFolderId(e.target.value)}
                placeholder="Google Drive folder ID"
                data-testid="drive-root-folder-id-input"
              />
            </div>
            <div>
              <Label htmlFor="drive-shared-drive-id" className="text-xs">Shared Drive ID (optional, personal My Drive if blank)</Label>
              <Input
                id="drive-shared-drive-id"
                value={driveId}
                onChange={(e) => setDriveId(e.target.value)}
                placeholder="Leave blank unless filing into a Shared Drive"
                data-testid="drive-shared-drive-id-input"
              />
            </div>
            <p className="text-xs text-muted-foreground">
              Validated live against Drive on save. Requires the {settings?.validation_account_id || 'pinned backend'} Google account to be connected.
            </p>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving} data-testid="drive-settings-save">
                {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving...</> : 'Save'}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </div>
        ) : configured ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="success" className="bg-green-500/10 text-green-600 border-green-500/30">
                <Check className="w-3 h-3 mr-1" /> Configured
              </Badge>
              <span className="font-medium">{settings.root_folder_name || settings.root_folder_id}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {settings.drive_id
                ? `Shared Drive: ${settings.drive_name || settings.drive_id}`
                : 'Personal My Drive (no Shared Drive)'}
            </p>
            <p className="text-xs text-muted-foreground">
              Set by {settings.configured_by} · updated {settings.updated_at ? new Date(settings.updated_at).toLocaleString() : 'unknown'}
            </p>
            <Button size="sm" variant="outline" onClick={startEditing} data-testid="drive-settings-edit">
              Change destination
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Badge variant="secondary" className="text-muted-foreground">
                <AlertCircle className="w-3 h-3 mr-1" /> Not configured
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              No destination root is set. Invoice filing to Drive will fail until one is configured here.
            </p>
            <Button size="sm" onClick={startEditing} data-testid="drive-settings-configure">
              Configure destination
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Pennylane Dashboard Component
function PennylaneDashboard({ status, onSync, syncHistory, invoices, schedulerStatus, schedulerConfig, onUpdateScheduler }) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncingRecon, setIsSyncingRecon] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSchedulerSettings, setShowSchedulerSettings] = useState(false);
  const [localConfig, setLocalConfig] = useState(schedulerConfig);
  const [isSaving, setIsSaving] = useState(false);
  
  useEffect(() => {
    setLocalConfig(schedulerConfig);
  }, [schedulerConfig]);
  
  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await onSync();
    } finally {
      setIsSyncing(false);
    }
  };
  
  const handleSaveScheduler = async () => {
    setIsSaving(true);
    try {
      await onUpdateScheduler(localConfig);
      setShowSchedulerSettings(false);
      toast.success('Scheduler settings updated');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };
  
  const lastSync = syncHistory?.[0];
  const nextRun = schedulerStatus?.next_run ? new Date(schedulerStatus.next_run) : null;
  
  return (
    <div className="space-y-4">
      {/* Status Card */}
      <Card className="border-green-500/30 bg-green-500/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Pennylane Connected</CardTitle>
                <CardDescription>
                  {(status?.entities || [])
                    .filter((e) => e.connected)
                    .map((e) => e.company_name || e.canonical_name)
                    .join(', ') || `${status?.connected_count ?? 0} of ${status?.total_count ?? 0} entities connected`}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowSchedulerSettings(true)} data-testid="scheduler-settings-btn">
                <Settings2 className="w-4 h-4 mr-1" />
                Auto-Sync
              </Button>
              <Button onClick={handleSync} disabled={isSyncing} data-testid="pennylane-sync-btn">
                {isSyncing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Sync Now
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  setIsSyncingRecon(true);
                  try {
                    const { data } = await pennylaneAPI.syncReconciliation();
                    if (data.synced > 0) {
                      toast.success(`Pushed ${data.synced} reconciled invoice${data.synced !== 1 ? 's' : ''} to Pennylane`);
                    } else if (data.failed > 0) {
                      toast.error(`Failed to sync ${data.failed} invoice${data.failed !== 1 ? 's' : ''}`);
                    } else if (!data.refused) {
                      toast.info('No pending reconciliations to sync');
                    }
                    if (data.refused > 0) {
                      toast.warning(
                        `Refused ${data.refused} invoice${data.refused !== 1 ? 's' : ''} with a rejected/quarantined review status — not pushed to Pennylane`
                      );
                    }
                  } catch (error) {
                    toast.error(getErrorMessage(error));
                  } finally {
                    setIsSyncingRecon(false);
                  }
                }}
                disabled={isSyncingRecon}
              >
                {isSyncingRecon ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-2" />Sync Reconciliation</>
                )}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {lastSync && (
              <>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Last sync: {new Date(lastSync.timestamp).toLocaleString()}
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  {lastSync.invoices_synced} invoices, {lastSync.expenses_synced} expenses
                </div>
              </>
            )}
            {schedulerConfig?.enabled && nextRun && (
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30">
                  <Zap className="w-3 h-3 mr-1" />
                  Auto-sync every {schedulerConfig.interval_minutes}min
                </Badge>
                <span className="text-xs">
                  Next: {nextRun.toLocaleTimeString()}
                </span>
              </div>
            )}
            {!schedulerConfig?.enabled && (
              <Badge variant="secondary">
                Auto-sync disabled
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* Scheduler Settings Dialog */}
      <Dialog open={showSchedulerSettings} onOpenChange={setShowSchedulerSettings}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Auto-Sync Settings</DialogTitle>
            <DialogDescription>
              Configure automatic synchronization for Pennylane data
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-6">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="auto-sync-enabled">Enable Auto-Sync</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically sync data at regular intervals
                </p>
              </div>
              <Switch
                id="auto-sync-enabled"
                checked={localConfig?.enabled ?? true}
                onCheckedChange={(checked) => setLocalConfig(prev => ({ ...prev, enabled: checked }))}
                data-testid="auto-sync-toggle"
              />
            </div>
            
            {localConfig?.enabled && (
              <div className="space-y-2">
                <Label>Sync Interval</Label>
                <Select
                  value={String(localConfig?.interval_minutes || 30)}
                  onValueChange={(value) => setLocalConfig(prev => ({ ...prev, interval_minutes: parseInt(value) }))}
                >
                  <SelectTrigger data-testid="sync-interval-select">
                    <SelectValue placeholder="Select interval" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">Every 5 minutes</SelectItem>
                    <SelectItem value="15">Every 15 minutes</SelectItem>
                    <SelectItem value="30">Every 30 minutes</SelectItem>
                    <SelectItem value="60">Every hour</SelectItem>
                    <SelectItem value="120">Every 2 hours</SelectItem>
                    <SelectItem value="360">Every 6 hours</SelectItem>
                    <SelectItem value="720">Every 12 hours</SelectItem>
                    <SelectItem value="1440">Every 24 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {schedulerStatus?.running && (
              <Alert>
                <Calendar className="h-4 w-4" />
                <AlertDescription>
                  Scheduler is running. Next sync: {nextRun ? nextRun.toLocaleString() : 'Not scheduled'}
                </AlertDescription>
              </Alert>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSchedulerSettings(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveScheduler} disabled={isSaving}>
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Tabs for data views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="invoices">Invoices ({invoices?.filter(i => i.type === 'outbound').length || 0})</TabsTrigger>
          <TabsTrigger value="expenses">Expenses ({invoices?.filter(i => i.type === 'inbound').length || 0})</TabsTrigger>
          <TabsTrigger value="history">Sync History</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{invoices?.filter(i => i.type === 'outbound').length || 0}</div>
                <p className="text-sm text-muted-foreground">Customer Invoices</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{invoices?.filter(i => i.type === 'inbound').length || 0}</div>
                <p className="text-sm text-muted-foreground">Supplier Invoices</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-600">
                  {invoices?.filter(i => i.payment_status === 'paid').length || 0}
                </div>
                <p className="text-sm text-muted-foreground">Paid</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-amber-600">
                  {invoices?.filter(i => i.payment_status === 'pending').length || 0}
                </div>
                <p className="text-sm text-muted-foreground">Pending</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        <TabsContent value="invoices">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices?.filter(i => i.type === 'outbound').slice(0, 10).map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.invoice_number || '-'}</TableCell>
                      <TableCell>{invoice.supplier_client_name}</TableCell>
                      <TableCell>€{Number(invoice.amount).toFixed(2)}</TableCell>
                      <TableCell>{invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <Badge variant={invoice.payment_status === 'paid' ? 'default' : 'secondary'}>
                          {invoice.payment_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="expenses">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices?.filter(i => i.type === 'inbound').slice(0, 10).map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.invoice_number || '-'}</TableCell>
                      <TableCell>{invoice.supplier_client_name}</TableCell>
                      <TableCell>€{Number(invoice.amount).toFixed(2)}</TableCell>
                      <TableCell>{invoice.issue_date ? new Date(invoice.issue_date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        <Badge variant={invoice.payment_status === 'paid' ? 'default' : 'secondary'}>
                          {invoice.payment_status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="history">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Invoices</TableHead>
                    <TableHead>Expenses</TableHead>
                    <TableHead>Customers</TableHead>
                    <TableHead>Suppliers</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncHistory?.map((log, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{new Date(log.timestamp).toLocaleString()}</TableCell>
                      <TableCell>{log.invoices_synced}</TableCell>
                      <TableCell>{log.expenses_synced}</TableCell>
                      <TableCell>{log.customers_synced}</TableCell>
                      <TableCell>{log.suppliers_synced}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Xero Dashboard Component
function XeroDashboard({ status, onSync, onDisconnect, onReconnect }) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [isSyncingRecon, setIsSyncingRecon] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [showBookkeeperDialog, setShowBookkeeperDialog] = useState(false);
  const [bookkeeperEmail, setBookkeeperEmail] = useState('');
  const [isSendingReport, setIsSendingReport] = useState(false);

  useEffect(() => {
    xeroAPI.getSyncHistory().then(res => setSyncHistory(res.data.history || [])).catch((e) => { console.warn('Xero sync history unavailable:', e.message); toast.error('Could not load Xero sync history'); });
  }, []);

  const lastSync = syncHistory?.[0];

  const handleSendBookkeeperReport = async () => {
    if (!bookkeeperEmail.trim()) {
      toast.error('Please enter a recipient email');
      return;
    }
    setIsSendingReport(true);
    try {
      const { data } = await xeroAPI.sendBookkeeperReport(bookkeeperEmail.trim());
      if (data.sent) {
        toast.success(
          `Report sent to ${data.recipient}: ${data.invoices_reported} invoice(s), ${data.intercompany_reported} intercompany entry/ies`
        );
        setShowBookkeeperDialog(false);
      } else {
        toast.info(data.message || 'Nothing new to report');
        setShowBookkeeperDialog(false);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsSendingReport(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="border-green-500/30 bg-green-500/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Xero Connected</CardTitle>
                <CardDescription>
                  {status?.tenants?.length || 0} tenant(s): {status?.tenants?.map(t => t.name).join(', ') || '—'}
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={async () => {
                  setIsSyncing(true);
                  try {
                    await onSync();
                  } finally {
                    setIsSyncing(false);
                  }
                }}
                disabled={isSyncing}
              >
                {isSyncing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" />Sync Now</>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  setIsSyncingRecon(true);
                  try {
                    const { data } = await xeroAPI.syncReconciliation();
                    if (data.synced > 0) {
                      toast.success(`Pushed ${data.synced} reconciled invoice${data.synced !== 1 ? 's' : ''} to Xero`);
                    } else if (data.failed > 0) {
                      toast.error(`Failed to sync ${data.failed} invoice${data.failed !== 1 ? 's' : ''}`);
                    } else if (!data.refused) {
                      toast.info('No pending reconciliations to sync');
                    }
                    if (data.refused > 0) {
                      toast.warning(
                        `Refused ${data.refused} invoice${data.refused !== 1 ? 's' : ''} with a rejected/quarantined review status — not pushed to Xero`
                      );
                    }
                  } catch (error) {
                    toast.error(getErrorMessage(error));
                  } finally {
                    setIsSyncingRecon(false);
                  }
                }}
                disabled={isSyncingRecon}
              >
                {isSyncingRecon ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-2" />Sync Reconciliation</>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowBookkeeperDialog(true)}
              >
                <Mail className="w-4 h-4 mr-2" />
                Email Bookkeeper
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onReconnect}
                title="Re-run Xero OAuth. Use this if the connection shows Connected here but the stored token is actually dead (revoked/expired on Xero's side) -- no need to disconnect first."
                data-testid="xero-reconnect"
              >
                <Link2 className="w-3.5 h-3.5 mr-1.5" />
                Reconnect
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onDisconnect}
                className="text-destructive hover:text-destructive"
              >
                Disconnect
              </Button>
            </div>
          </div>
        </CardHeader>
        {shouldShowXeroFinanceScopeReconsentBanner(status) && <XeroFinanceScopeReconsentBanner />}
        <CardContent>
          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            {lastSync && (
              <>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Last sync: {new Date(lastSync.timestamp).toLocaleString()}
                </div>
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" />
                  {lastSync.invoices_synced} invoices synced
                </div>
              </>
            )}
          </div>
          <div className="mt-2">
            <XeroQuotaMiniBadge />
          </div>
        </CardContent>
      </Card>

      {/* Bookkeeper Report Dialog */}
      <Dialog open={showBookkeeperDialog} onOpenChange={setShowBookkeeperDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Bookkeeper Report</DialogTitle>
            <DialogDescription>
              Send an email with all reconciled invoices and intercompany payments that haven't been reported yet.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bookkeeper-email">Recipient Email</Label>
              <Input
                id="bookkeeper-email"
                type="email"
                placeholder="bookkeeper@example.com"
                value={bookkeeperEmail}
                onChange={(e) => setBookkeeperEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSendBookkeeperReport()}
              />
            </div>
            <Alert>
              <Mail className="h-4 w-4" />
              <AlertDescription>
                The email will be sent from your connected Google account and will include all new reconciled invoices and intercompany entries since the last report.
              </AlertDescription>
            </Alert>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBookkeeperDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendBookkeeperReport} disabled={isSendingReport || !bookkeeperEmail.trim()}>
              {isSendingReport ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending...</>
              ) : (
                <><Mail className="w-4 h-4 mr-2" />Send Report</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Uplisting Dashboard Component
function UplistingDashboard({ status, onSync, onDisconnect }) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [properties, setProperties] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [forecastData, setForecastData] = useState(null);
  const [channelFilter, setChannelFilter] = useState('all');
  const [updatingEntity, setUpdatingEntity] = useState(null);
  const allEntities = useEntityStore((s) => s.entities);

  const handlePropertyConfig = async (propertyId, updates) => {
    setUpdatingEntity(propertyId);
    try {
      const prop = properties.find((p) => p.uplisting_id === propertyId);
      const config = {
        entity_id: prop?.entity_uuid || updates.entity_id,
        management_type: prop?.management_type || 'owned',
        management_fee_rate: prop?.management_fee_rate ?? null,
        ...updates,
      };
      if (!config.entity_id) {
        toast.error('Please assign an entity first');
        setUpdatingEntity(null);
        return;
      }
      await uplistingAPI.updatePropertyConfig(propertyId, config);
      setProperties((prev) =>
        prev.map((p) => {
          if (p.uplisting_id !== propertyId) return p;
          const entity = updates.entity_id
            ? allEntities.find((e) => e.id === updates.entity_id)
            : null;
          return {
            ...p,
            ...(updates.entity_id && { entity_uuid: updates.entity_id, entity_name: entity?.name ?? p.entity_name }),
            ...(updates.management_type !== undefined && { management_type: updates.management_type }),
            ...(updates.management_fee_rate !== undefined && { management_fee_rate: updates.management_fee_rate }),
          };
        })
      );
      toast.success('Property configuration updated');
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Failed to update property');
    } finally {
      setUpdatingEntity(null);
    }
  };

  const handlePortfolioToggle = async (propertyId, currentExcluded) => {
    const newValue = !currentExcluded;
    // Optimistic update
    setProperties((prev) =>
      prev.map((p) => p.uplisting_id === propertyId
        ? { ...p, excluded_from_portfolio: newValue }
        : p)
    );
    try {
      await portfolioPropertiesAPI.update(propertyId, { excluded_from_portfolio: newValue });
    } catch (e) {
      // Rollback
      setProperties((prev) =>
        prev.map((p) => p.uplisting_id === propertyId
          ? { ...p, excluded_from_portfolio: currentExcluded }
          : p)
      );
      toast.error(getErrorMessage(e) || 'Failed to update portfolio inclusion');
    }
  };

  useEffect(() => {
    if (status?.connected) {
      Promise.all([
        uplistingAPI.getProperties(),
        uplistingAPI.getReservations({ limit: 100 }),
        uplistingAPI.getRevenueSummary(),
        uplistingAPI.getCashflowForecast(),
      ]).then(([propRes, resRes, revRes, forecastRes]) => {
        setProperties(propRes.data.properties || []);
        setReservations(resRes.data.reservations || []);
        setRevenueSummary(revRes.data);
        setForecastData(forecastRes.data);
      }).catch(e => { console.warn('Uplisting data load:', e.message); });
    }
  }, [status]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await onSync();
      const [propRes, resRes, revRes, forecastRes] = await Promise.all([
        uplistingAPI.getProperties(),
        uplistingAPI.getReservations({ limit: 100 }),
        uplistingAPI.getRevenueSummary(),
        uplistingAPI.getCashflowForecast(),
      ]);
      setProperties(propRes.data.properties || []);
      setReservations(resRes.data.reservations || []);
      setRevenueSummary(revRes.data);
      setForecastData(forecastRes.data);
    } finally {
      setIsSyncing(false);
    }
  };

  // Display-layer fallback for guest name: ensures legacy rows with blank
  // guest_name still show a deterministic label rather than an em dash.
  const guestDisplayName = (res) => {
    if (res.guest_name) return res.guest_name;
    const channelLabel = { airbnb: 'Airbnb', booking_com: 'Booking.com', direct: 'Direct', vrbo: 'Vrbo' }[res.channel] || (res.channel || 'Unknown').replace(/_/g, ' ');
    const extId = res.external_reservation_id || res.uplisting_id || res.smoobu_id || '';
    const suffix = extId.length >= 4 ? extId.slice(-4) : extId;
    return suffix ? `${channelLabel} guest ...${suffix}` : `${channelLabel} guest`;
  };

  const channelBadge = (channel) => {
    const colors = {
      airbnb: 'bg-pink-500/10 text-pink-600 border-pink-500/30',
      booking_com: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
      direct: 'bg-green-500/10 text-green-600 border-green-500/30',
      vrbo: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
    };
    const labels = { airbnb: 'Airbnb', booking_com: 'Booking.com', direct: 'Direct', vrbo: 'Vrbo' };
    return (
      <Badge variant="outline" className={`text-xs ${colors[channel] || ''}`}>
        {labels[channel] || channel}
      </Badge>
    );
  };

  const filteredReservations = channelFilter === 'all'
    ? reservations
    : reservations.filter(r => r.channel === channelFilter);

  const unassignedCount = properties.filter(p => !p.entity_uuid).length;

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <Card className="border-green-500/30 bg-green-500/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Uplisting Connected</CardTitle>
                <CardDescription>
                  {properties.length} properties, {reservations.length} reservations synced
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={handleSync} disabled={isSyncing}>
                {isSyncing ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
                ) : (
                  <><RefreshCw className="w-4 h-4 mr-2" />Sync Now</>
                )}
              </Button>
              <Button size="sm" variant="ghost" onClick={onDisconnect} className="text-destructive hover:text-destructive">
                Disconnect
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Unassigned warning */}
      {unassignedCount > 0 && (
        <Alert>
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            {unassignedCount} {unassignedCount === 1 ? 'property needs' : 'properties need'} entity assignment.
            Go to the Properties tab to assign each property to a business entity.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="properties">Properties ({properties.length})</TabsTrigger>
          <TabsTrigger value="reservations">Reservations ({reservations.length})</TabsTrigger>
          <TabsTrigger value="cashflow">Cashflow Forecast</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{properties.length}</div>
                <p className="text-sm text-muted-foreground">Properties</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold">{reservations.filter(r => r.status === 'confirmed').length}</div>
                <p className="text-sm text-muted-foreground">Active Reservations</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-green-600">
                  {revenueSummary ? `€${revenueSummary.total_net_revenue?.toLocaleString()}` : '—'}
                </div>
                <p className="text-sm text-muted-foreground">Net Revenue</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-2xl font-bold text-blue-600">
                  {revenueSummary ? `€${revenueSummary.total_br_commission?.toLocaleString()}` : '—'}
                </div>
                <p className="text-sm text-muted-foreground">BR Commission</p>
              </CardContent>
            </Card>
          </div>
          {revenueSummary?.by_channel && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Revenue by Channel</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(revenueSummary.by_channel).map(([ch, data]) => (
                    <div key={ch} className="p-3 rounded-lg border">
                      {channelBadge(ch)}
                      <div className="mt-2 text-lg font-semibold">€{data.net?.toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">{data.count} bookings</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="properties">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Beds</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Channels</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Portfolio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.map((prop) => (
                    <TableRow key={prop.uplisting_id}>
                      <TableCell className="font-medium">{prop.name}</TableCell>
                      <TableCell>{prop.city}</TableCell>
                      <TableCell>{prop.bedrooms}</TableCell>
                      <TableCell>
                        <Badge variant={prop.status === 'active' ? 'default' : 'secondary'}>
                          {prop.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {prop.listing_urls?.airbnb && (
                            <Badge variant="outline" className="text-xs bg-pink-500/10 text-pink-600">Airbnb</Badge>
                          )}
                          {prop.listing_urls?.booking_com && (
                            <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600">Booking</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={prop.entity_uuid ?? ''}
                          onValueChange={(val) => handlePropertyConfig(prop.uplisting_id, { entity_id: val })}
                          disabled={updatingEntity === prop.uplisting_id}
                        >
                          <SelectTrigger className="w-[160px] h-8 text-xs">
                            <SelectValue placeholder="Assign entity...">
                              {updatingEntity === prop.uplisting_id
                                ? 'Saving...'
                                : (prop.entity_name || 'Assign entity...')}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {allEntities.map((e) => (
                              <SelectItem key={e.id} value={e.id} className="text-xs">
                                {e.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={prop.management_type || 'owned'}
                          onValueChange={(val) => handlePropertyConfig(prop.uplisting_id, {
                            management_type: val,
                            management_fee_rate: val === 'managed' ? (prop.management_fee_rate ?? 0.20) : null,
                          })}
                          disabled={updatingEntity === prop.uplisting_id || !prop.entity_uuid}
                        >
                          <SelectTrigger className="w-[110px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owned" className="text-xs">Owned</SelectItem>
                            <SelectItem value="managed" className="text-xs">Managed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {prop.management_type === 'managed' ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              className="w-14 h-8 text-xs border rounded px-1 text-center"
                              value={prop.management_fee_rate != null ? Math.round(prop.management_fee_rate * 100) : 20}
                              min={1}
                              max={100}
                              onChange={(e) => {
                                const rate = parseInt(e.target.value, 10);
                                if (!isNaN(rate)) {
                                  setProperties((prev) =>
                                    prev.map((p) => p.uplisting_id === prop.uplisting_id
                                      ? { ...p, management_fee_rate: rate / 100 }
                                      : p)
                                  );
                                }
                              }}
                              onBlur={(e) => {
                                const rate = parseInt(e.target.value, 10);
                                if (!isNaN(rate) && rate > 0 && rate <= 100) {
                                  handlePropertyConfig(prop.uplisting_id, { management_fee_rate: rate / 100 });
                                }
                              }}
                            />
                            <span className="text-xs text-muted-foreground">%</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!prop.excluded_from_portfolio}
                            onCheckedChange={() => handlePortfolioToggle(prop.uplisting_id, !!prop.excluded_from_portfolio)}
                          />
                          <span className="text-xs text-muted-foreground">
                            {prop.excluded_from_portfolio ? 'Excluded' : 'Included'}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reservations">
          <div className="flex items-center gap-2 mb-4">
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Channels</SelectItem>
                <SelectItem value="airbnb">Airbnb</SelectItem>
                <SelectItem value="booking_com">Booking.com</SelectItem>
                <SelectItem value="direct">Direct</SelectItem>
                <SelectItem value="vrbo">Vrbo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Guest</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Check-in</TableHead>
                    <TableHead>Check-out</TableHead>
                    <TableHead>Nights</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Net Revenue</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Expected Payout</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReservations.slice(0, 50).map((res) => (
                    <TableRow key={res.uplisting_id}>
                      <TableCell className="font-medium">{guestDisplayName(res)}</TableCell>
                      <TableCell>{res.property_name || '—'}</TableCell>
                      <TableCell>{channelBadge(res.channel)}</TableCell>
                      <TableCell>{res.check_in ? new Date(res.check_in).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{res.check_out ? new Date(res.check_out).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{res.nights}</TableCell>
                      <TableCell>€{Number(res.total_price || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-green-600">€{Number(res.net_revenue || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge variant={res.status === 'confirmed' ? 'default' : res.status === 'completed' ? 'secondary' : 'destructive'}>
                          {res.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {res.expected_payout_date ? new Date(res.expected_payout_date).toLocaleDateString() : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="cashflow" className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Expected Incoming Payments</h3>
            <p className="text-sm text-muted-foreground">
              Next {forecastData?.summary?.weeks_count || 12} weeks
            </p>
          </div>

          {forecastData?.summary && (
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-green-600">
                    €{forecastData.summary.total_forecast?.toLocaleString()}
                  </div>
                  <p className="text-sm text-muted-foreground">Total Forecast</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">
                    {forecastData.summary.total_reservations}
                  </div>
                  <p className="text-sm text-muted-foreground">Reservations</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-blue-600">
                    €{forecastData.summary.avg_weekly?.toLocaleString()}
                  </div>
                  <p className="text-sm text-muted-foreground">Avg / Week</p>
                </CardContent>
              </Card>
            </div>
          )}

          {forecastData?.weeks?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Weekly Expected Payouts</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={forecastData.weeks} margin={{ top: 30, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis
                      dataKey="week_label"
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={11}
                      angle={-30}
                      textAnchor="end"
                      height={60}
                    />
                    <YAxis
                      stroke="hsl(var(--muted-foreground))"
                      fontSize={12}
                      tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`}
                    />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'hsl(var(--card))',
                        border: '1px solid hsl(var(--border))',
                        borderRadius: '8px',
                      }}
                      formatter={(value) => [`€${Number(value).toLocaleString()}`, '']}
                    />
                    <Legend />
                    <Bar dataKey="by_channel.airbnb" name="Airbnb" stackId="a" fill="#FF5A5F">
                      <LabelList
                        dataKey="by_channel.airbnb"
                        position="center"
                        formatter={(v) => v >= 1000 ? formatCompact(v, 'EUR') : ''}
                        style={{ fontSize: 11, fontWeight: 600, fill: '#ffffff' }}
                      />
                    </Bar>
                    <Bar dataKey="by_channel.booking_com" name="Booking.com" stackId="a" fill="#003580">
                      <LabelList
                        dataKey="by_channel.booking_com"
                        position="center"
                        formatter={(v) => v >= 1000 ? formatCompact(v, 'EUR') : ''}
                        style={{ fontSize: 11, fontWeight: 600, fill: '#ffffff' }}
                      />
                    </Bar>
                    <Bar dataKey="by_channel.direct" name="Direct" stackId="a" fill="#10b981" radius={[4, 4, 0, 0]}>
                      <LabelList
                        dataKey="by_channel.direct"
                        position="center"
                        formatter={(v) => v >= 1000 ? formatCompact(v, 'EUR') : ''}
                        style={{ fontSize: 11, fontWeight: 600, fill: '#ffffff' }}
                      />
                      <LabelList
                        position="top"
                        content={(props) => {
                          const { x, y, width, index } = props;
                          const row = forecastData.weeks[index];
                          const ch = row?.by_channel || {};
                          const total = (ch.airbnb || 0) + (ch.booking_com || 0) + (ch.direct || 0);
                          if (!total) return null;
                          return (
                            <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={11} fontWeight={600} fill="#1f2937">
                              {formatCompact(total, 'EUR')}
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {forecastData?.weeks?.length > 0 && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Weekly Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Week</TableHead>
                      <TableHead className="text-right">Reservations</TableHead>
                      <TableHead className="text-right">Airbnb</TableHead>
                      <TableHead className="text-right">Booking.com</TableHead>
                      <TableHead className="text-right">Direct</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      <TableHead className="text-right">Cumulative</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {forecastData.weeks.map((week) => (
                      <TableRow key={week.week_start}>
                        <TableCell className="font-medium text-sm">{week.week_label}</TableCell>
                        <TableCell className="text-right">{week.reservation_count}</TableCell>
                        <TableCell className="text-right text-sm">€{(week.by_channel?.airbnb || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right text-sm">€{(week.by_channel?.booking_com || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right text-sm">€{(week.by_channel?.direct || 0).toLocaleString()}</TableCell>
                        <TableCell className="text-right font-medium text-green-600">€{week.total_amount?.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-muted-foreground">€{week.cumulative?.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {(!forecastData?.weeks || forecastData.weeks.length === 0) && (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No upcoming payouts found. Sync reservations to see forecast data.
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="revenue" className="space-y-4">
          {revenueSummary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">€{revenueSummary.total_gross_revenue?.toLocaleString()}</div>
                    <p className="text-sm text-muted-foreground">Gross Revenue</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-amber-600">€{revenueSummary.total_channel_commission?.toLocaleString()}</div>
                    <p className="text-sm text-muted-foreground">Channel Fees</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold text-green-600">€{revenueSummary.total_net_revenue?.toLocaleString()}</div>
                    <p className="text-sm text-muted-foreground">Net Revenue</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">€{revenueSummary.average_nightly_rate?.toFixed(0)}</div>
                    <p className="text-sm text-muted-foreground">Avg Nightly Rate</p>
                  </CardContent>
                </Card>
              </div>
              {revenueSummary.by_month && Object.keys(revenueSummary.by_month).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Revenue by Month</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          <TableHead>Bookings</TableHead>
                          <TableHead>Gross Revenue</TableHead>
                          <TableHead>Net Revenue</TableHead>
                          <TableHead>BR Commission</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(revenueSummary.by_month).map(([month, data]) => (
                          <TableRow key={month}>
                            <TableCell className="font-medium">{month}</TableCell>
                            <TableCell>{data.count}</TableCell>
                            <TableCell>€{data.gross?.toLocaleString()}</TableCell>
                            <TableCell className="text-green-600">€{data.net?.toLocaleString()}</TableCell>
                            <TableCell className="text-blue-600">€{data.commission?.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>

    </div>
  );
}

// Smoobu Dashboard Component
function SmoobuDashboard({ status, onSync }) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('properties');
  const [properties, setProperties] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [revenueSummary, setRevenueSummary] = useState(null);
  const [channelFilter, setChannelFilter] = useState('all');
  const [updatingEntity, setUpdatingEntity] = useState(null);
  const allEntities = useEntityStore((s) => s.entities);

  const handlePropertyConfig = async (propertyId, updates) => {
    setUpdatingEntity(propertyId);
    try {
      const prop = properties.find((p) => p.smoobu_id === propertyId);
      const config = {
        entity_id: prop?.entity_uuid || updates.entity_id,
        management_type: prop?.management_type || 'owned',
        management_fee_rate: prop?.management_fee_rate ?? null,
        ...updates,
      };
      if (!config.entity_id) {
        toast.error('Please assign an entity first');
        setUpdatingEntity(null);
        return;
      }
      await smoobuAPI.updatePropertyConfig(propertyId, config);
      setProperties((prev) =>
        prev.map((p) => {
          if (p.smoobu_id !== propertyId) return p;
          const entity = updates.entity_id
            ? allEntities.find((e) => e.id === updates.entity_id)
            : null;
          return {
            ...p,
            ...(updates.entity_id && { entity_uuid: updates.entity_id, entity_name: entity?.name ?? p.entity_name }),
            ...(updates.management_type !== undefined && { management_type: updates.management_type }),
            ...(updates.management_fee_rate !== undefined && { management_fee_rate: updates.management_fee_rate }),
          };
        })
      );
      toast.success('Property configuration updated');
    } catch (e) {
      toast.error(getErrorMessage(e) || 'Failed to update property');
    } finally {
      setUpdatingEntity(null);
    }
  };

  const handlePortfolioToggle = async (propertyId, currentExcluded) => {
    const newValue = !currentExcluded;
    // Optimistic update
    setProperties((prev) =>
      prev.map((p) => p.smoobu_id === propertyId
        ? { ...p, excluded_from_portfolio: newValue }
        : p)
    );
    try {
      await portfolioPropertiesAPI.update(propertyId, { excluded_from_portfolio: newValue });
    } catch (e) {
      // Rollback
      setProperties((prev) =>
        prev.map((p) => p.smoobu_id === propertyId
          ? { ...p, excluded_from_portfolio: currentExcluded }
          : p)
      );
      toast.error(getErrorMessage(e) || 'Failed to update portfolio inclusion');
    }
  };

  useEffect(() => {
    if (status?.connected) {
      Promise.all([
        smoobuAPI.getProperties(),
        smoobuAPI.getReservations({ limit: 100 }),
        smoobuAPI.getRevenueSummary(),
      ]).then(([propRes, resRes, revRes]) => {
        setProperties(propRes.data.properties || []);
        setReservations(resRes.data.reservations || []);
        setRevenueSummary(revRes.data);
      }).catch(e => { console.warn('Smoobu data load:', e.message); });
    }
  }, [status]);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await onSync();
      const [propRes, resRes, revRes] = await Promise.all([
        smoobuAPI.getProperties(),
        smoobuAPI.getReservations({ limit: 100 }),
        smoobuAPI.getRevenueSummary(),
      ]);
      setProperties(propRes.data.properties || []);
      setReservations(resRes.data.reservations || []);
      setRevenueSummary(revRes.data);
    } finally {
      setIsSyncing(false);
    }
  };

  const guestDisplayName = (res) => {
    if (res.guest_name) return res.guest_name;
    const channelLabel = { airbnb: 'Airbnb', booking_com: 'Booking.com', direct: 'Direct' }[res.channel] || (res.channel || 'Unknown').replace(/_/g, ' ');
    const extId = res.reference_id || res.smoobu_id || '';
    const suffix = extId.length >= 4 ? extId.slice(-4) : extId;
    return suffix ? `${channelLabel} guest ...${suffix}` : `${channelLabel} guest`;
  };

  const channelBadge = (channel) => {
    const colors = {
      airbnb: 'bg-pink-500/10 text-pink-600 border-pink-500/30',
      booking_com: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
      direct: 'bg-green-500/10 text-green-600 border-green-500/30',
    };
    const labels = { airbnb: 'Airbnb', booking_com: 'Booking.com', direct: 'Direct' };
    return (
      <Badge variant="outline" className={`text-xs ${colors[channel] || ''}`}>
        {labels[channel] || channel}
      </Badge>
    );
  };

  const filteredReservations = channelFilter === 'all'
    ? reservations
    : reservations.filter(r => r.channel === channelFilter);

  const unassignedCount = properties.filter(p => !p.entity_uuid).length;

  return (
    <div className="space-y-4">
      {/* Status Card */}
      <Card className="border-green-500/30 bg-green-500/5">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <CardTitle className="text-lg">Smoobu Connected</CardTitle>
                <CardDescription>
                  {properties.length} properties, {reservations.length} reservations synced
                </CardDescription>
              </div>
            </div>
            <Button onClick={handleSync} disabled={isSyncing}>
              {isSyncing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Syncing...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-2" />Sync Now</>
              )}
            </Button>
          </div>
        </CardHeader>
      </Card>

      {/* Unassigned warning */}
      {unassignedCount > 0 && (
        <Alert>
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            {unassignedCount} {unassignedCount === 1 ? 'property needs' : 'properties need'} entity assignment.
            Go to the Properties tab to assign each property to a business entity.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="properties">
            Properties ({properties.length})
            {unassignedCount > 0 && (
              <Badge variant="destructive" className="ml-1.5 h-5 px-1.5 text-xs">{unassignedCount}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reservations">Reservations ({reservations.length})</TabsTrigger>
          <TabsTrigger value="revenue">Revenue</TabsTrigger>
        </TabsList>

        <TabsContent value="properties">
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Property</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Beds</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Fee</TableHead>
                    <TableHead>Portfolio</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {properties.map((prop) => (
                    <TableRow key={prop.smoobu_id}>
                      <TableCell className="font-medium max-w-[280px] truncate" title={prop.name}>
                        {prop.name}
                      </TableCell>
                      <TableCell>{prop.city || '—'}</TableCell>
                      <TableCell>{prop.rooms?.bedrooms || '—'}</TableCell>
                      <TableCell>
                        <Select
                          value={prop.entity_uuid ?? ''}
                          onValueChange={(val) => handlePropertyConfig(prop.smoobu_id, { entity_id: val })}
                          disabled={updatingEntity === prop.smoobu_id}
                        >
                          <SelectTrigger className={`w-[180px] h-8 text-xs ${!prop.entity_uuid ? 'border-amber-500/50' : ''}`}>
                            <SelectValue placeholder="Assign entity...">
                              {updatingEntity === prop.smoobu_id
                                ? 'Saving...'
                                : (prop.entity_name || 'Assign entity...')}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {allEntities.map((e) => (
                              <SelectItem key={e.id} value={e.id} className="text-xs">
                                {e.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={prop.management_type || 'owned'}
                          onValueChange={(val) => handlePropertyConfig(prop.smoobu_id, {
                            management_type: val,
                            management_fee_rate: val === 'managed' ? (prop.management_fee_rate ?? 0.20) : null,
                          })}
                          disabled={updatingEntity === prop.smoobu_id || !prop.entity_uuid}
                        >
                          <SelectTrigger className="w-[110px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="owned" className="text-xs">Owned</SelectItem>
                            <SelectItem value="managed" className="text-xs">Managed</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {prop.management_type === 'managed' ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              className="w-14 h-8 text-xs border rounded px-1 text-center"
                              value={prop.management_fee_rate != null ? Math.round(prop.management_fee_rate * 100) : 20}
                              min={1}
                              max={100}
                              onChange={(e) => {
                                const rate = parseInt(e.target.value, 10);
                                if (!isNaN(rate)) {
                                  setProperties((prev) =>
                                    prev.map((p) => p.smoobu_id === prop.smoobu_id
                                      ? { ...p, management_fee_rate: rate / 100 }
                                      : p)
                                  );
                                }
                              }}
                              onBlur={(e) => {
                                const rate = parseInt(e.target.value, 10);
                                if (!isNaN(rate) && rate > 0 && rate <= 100) {
                                  handlePropertyConfig(prop.smoobu_id, { management_fee_rate: rate / 100 });
                                }
                              }}
                            />
                            <span className="text-xs text-muted-foreground">%</span>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={!prop.excluded_from_portfolio}
                            onCheckedChange={() => handlePortfolioToggle(prop.smoobu_id, !!prop.excluded_from_portfolio)}
                          />
                          <span className="text-xs text-muted-foreground">
                            {prop.excluded_from_portfolio ? 'Excluded' : 'Included'}
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="reservations">
          <div className="flex items-center gap-2 mb-4">
            <Select value={channelFilter} onValueChange={setChannelFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filter by channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Channels</SelectItem>
                <SelectItem value="airbnb">Airbnb</SelectItem>
                <SelectItem value="booking_com">Booking.com</SelectItem>
                <SelectItem value="direct">Direct</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Guest</TableHead>
                    <TableHead>Property</TableHead>
                    <TableHead>Channel</TableHead>
                    <TableHead>Check-in</TableHead>
                    <TableHead>Check-out</TableHead>
                    <TableHead>Nights</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Entity</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredReservations.slice(0, 50).map((res) => (
                    <TableRow key={res.smoobu_id}>
                      <TableCell className="font-medium">{guestDisplayName(res)}</TableCell>
                      <TableCell className="max-w-[200px] truncate" title={res.property_name}>{res.property_name || '—'}</TableCell>
                      <TableCell>{channelBadge(res.channel)}</TableCell>
                      <TableCell>{res.check_in ? new Date(res.check_in).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{res.check_out ? new Date(res.check_out).toLocaleDateString() : '—'}</TableCell>
                      <TableCell>{res.nights}</TableCell>
                      <TableCell>{res.currency === 'GBP' ? '£' : '€'}{Number(res.total_price || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-xs">{res.entity_name || '—'}</TableCell>
                      <TableCell>
                        <Badge variant={res.status === 'confirmed' ? 'default' : res.status === 'cancelled' ? 'destructive' : 'secondary'}>
                          {res.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="revenue" className="space-y-4">
          {revenueSummary && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">£{revenueSummary.total_gross_revenue?.toLocaleString()}</div>
                    <p className="text-sm text-muted-foreground">Gross Revenue</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{revenueSummary.reservation_count}</div>
                    <p className="text-sm text-muted-foreground">Reservations</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">£{revenueSummary.average_nightly_rate?.toFixed(0)}</div>
                    <p className="text-sm text-muted-foreground">Avg Nightly Rate</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <div className="text-2xl font-bold">{revenueSummary.average_stay_length?.toFixed(1)} nights</div>
                    <p className="text-sm text-muted-foreground">Avg Stay Length</p>
                  </CardContent>
                </Card>
              </div>
              {revenueSummary.by_month && Object.keys(revenueSummary.by_month).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Revenue by Month</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Month</TableHead>
                          <TableHead>Bookings</TableHead>
                          <TableHead>Gross Revenue</TableHead>
                          <TableHead>Commission</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(revenueSummary.by_month).map(([month, data]) => (
                          <TableRow key={month}>
                            <TableCell className="font-medium">{month}</TableCell>
                            <TableCell>{data.count}</TableCell>
                            <TableCell>£{data.gross?.toLocaleString()}</TableCell>
                            <TableCell className="text-blue-600">£{data.commission?.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
              {revenueSummary.by_property && Object.keys(revenueSummary.by_property).length > 0 && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">Revenue by Property</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Property</TableHead>
                          <TableHead>Bookings</TableHead>
                          <TableHead>Gross Revenue</TableHead>
                          <TableHead>Commission</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {Object.entries(revenueSummary.by_property)
                          .sort((a, b) => b[1].gross - a[1].gross)
                          .map(([prop, data]) => (
                          <TableRow key={prop}>
                            <TableCell className="font-medium max-w-[250px] truncate" title={prop}>{prop}</TableCell>
                            <TableCell>{data.count}</TableCell>
                            <TableCell>£{data.gross?.toLocaleString()}</TableCell>
                            <TableCell className="text-blue-600">£{data.commission?.toLocaleString()}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Hostaway Dashboard Component
//
// Status summary + a single link to Configure (HostawayListingMapModal --
// the same ownership-aware component used from Diagnostics). Ownership and
// entity assignment are edited there only; this panel never writes listing
// state itself, so there is exactly one place that can diverge from the
// owned/entity-uuid truth on hostaway_properties.
export function HostawayDashboard({ status, onSync }) {
  const [isSyncing, setIsSyncing] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [dedupStatus, setDedupStatus] = useState(null);
  const [configureOpen, setConfigureOpen] = useState(false);

  const loadSummary = async () => {
    try {
      const res = await hostawayAPI.diagnostics();
      setSummary(res.data);
    } catch (e) {
      console.error('Error loading Hostaway diagnostics:', e);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const [, dedupRes] = await Promise.allSettled([
          loadSummary(),
          hostawayAPI.dedupStatus(),
        ]);
        if (dedupRes.status === 'fulfilled') setDedupStatus(dedupRes.value.data);
      } catch (e) {
        console.error('Error loading Hostaway data:', e);
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await onSync();
      await loadSummary();
    } finally {
      setIsSyncing(false);
    }
  };

  const handleDedup = async () => {
    try {
      toast.info('Running Hostaway dedup against Smoobu...');
      const res = await hostawayAPI.dedup();
      const d = res.data;
      toast.success(`Dedup complete: ${d.superseded_count || 0} Smoobu bookings superseded`);
      const dedupRes = await hostawayAPI.dedupStatus();
      setDedupStatus(dedupRes.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const ownership = summary?.ownership;
  const ownedUnassigned = ownership?.owned_unassigned ?? 0;
  const cutoverDate = summary?.cutover_date;
  const lastSyncAt = summary?.last_sync?.hostaway?.started_at;

  return (
    <div className="space-y-4">
      <Alert className="border-blue-500/30 bg-blue-500/5">
        <AlertCircle className="w-4 h-4" />
        <AlertDescription>
          Reservation contract confirmed against the live Hostaway payload (2026-07);
          sync runs automatically every 60 minutes. Listing ownership, entity
          assignment, Smoobu mapping, and foreign-listing cleanup are all managed in
          Configure below{cutoverDate ? ` (Smoobu→Hostaway cutover: ${cutoverDate})` : ''}.
        </AlertDescription>
      </Alert>

      {ownedUnassigned > 0 && (
        <Alert>
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            {ownedUnassigned} owned {ownedUnassigned === 1 ? 'listing needs' : 'listings need'} entity assignment.
            A not-owned (foreign) listing is intentionally skipped and does not count here.
            Blocked from revenue reporting until assigned -- use Configure below.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2 mb-4">
        <Button size="sm" variant={activeTab === 'summary' ? 'default' : 'outline'} onClick={() => setActiveTab('summary')}>
          Summary
        </Button>
        <Button size="sm" variant={activeTab === 'dedup' ? 'default' : 'outline'} onClick={() => setActiveTab('dedup')}>
          Dedup Status
        </Button>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setConfigureOpen(true)} data-testid="hostaway-open-configure-btn">
            <Settings2 className="w-4 h-4 mr-1" /> Configure
          </Button>
          <Button size="sm" onClick={handleSync} disabled={isSyncing}>
            {isSyncing ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
      </div>

      {activeTab === 'summary' && (
        <Card>
          <CardContent className="p-4">
            {!summary ? (
              <p className="text-sm text-muted-foreground">Loading...</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm" data-testid="hostaway-ownership-summary">
                <div>
                  <p className="font-medium text-lg tabular-nums" data-testid="hostaway-summary-owned">{ownership?.owned ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Owned</p>
                </div>
                <div>
                  <p className="font-medium text-lg tabular-nums" data-testid="hostaway-summary-active">{ownership?.owned_assigned ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Active (owned + assigned)</p>
                </div>
                <div>
                  <p className="font-medium text-lg tabular-nums" data-testid="hostaway-summary-blocked">{ownership?.owned_unassigned ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Blocked (owned, unassigned)</p>
                </div>
                <div>
                  <p className="font-medium text-lg tabular-nums" data-testid="hostaway-summary-skipped">{ownership?.not_owned ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Not owned (skipped)</p>
                </div>
                <div>
                  <p className="font-medium text-lg tabular-nums" data-testid="hostaway-summary-skip-reservations">{ownership?.skip_reservation_count ?? 0}</p>
                  <p className="text-xs text-muted-foreground">Reservations skipped</p>
                </div>
              </div>
            )}
            {lastSyncAt && (
              <p className="text-xs text-muted-foreground mt-4">
                Last sync: {new Date(lastSyncAt).toLocaleString()}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {activeTab === 'dedup' && (
        <Card>
          <CardContent className="p-4 space-y-3">
            <p className="text-sm text-muted-foreground">
              Dedup compares Hostaway bookings against Smoobu and marks matched Smoobu bookings as superseded.
            </p>
            {dedupStatus ? (
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="font-medium">{dedupStatus.hostaway_count ?? '-'}</p>
                  <p className="text-xs text-muted-foreground">Hostaway bookings</p>
                </div>
                <div>
                  <p className="font-medium">{dedupStatus.superseded_count ?? '-'}</p>
                  <p className="text-xs text-muted-foreground">Smoobu superseded</p>
                </div>
                <div>
                  <p className="font-medium">{dedupStatus.last_run ?? 'never'}</p>
                  <p className="text-xs text-muted-foreground">Last dedup run</p>
                </div>
              </div>
            ) : (
              <p className="text-sm">No dedup data available.</p>
            )}
            <Button size="sm" variant="outline" onClick={handleDedup}>
              Run Dedup Now
            </Button>
          </CardContent>
        </Card>
      )}

      <HostawayListingMapModal
        open={configureOpen}
        onOpenChange={setConfigureOpen}
        onSaved={loadSummary}
      />
    </div>
  );
}

// Integration card component
function IntegrationCard({ integration, onConnect, onDisconnect, onConfigure, onViewDetails }) {
  const isConnected = integration.status === 'connected';
  const isReal = integration.isReal;
  
  return (
    <Card 
      className={`transition-all hover:shadow-md ${isConnected ? 'border-green-500/30 bg-green-500/5' : ''}`}
      data-testid={`integration-card-${integration.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
            <img 
              src={integration.logo} 
              alt={integration.name} 
              className="w-8 h-8 object-contain"
              onError={(e) => {
                e.target.style.display = 'none';
                const span = document.createElement('span');
                span.className = 'text-lg font-bold';
                span.textContent = integration.name.charAt(0);
                e.target.parentElement.appendChild(span);
              }}
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold truncate">{integration.name}</h3>
              {isReal && (
                <Badge variant="outline" className="text-xs bg-blue-500/10 text-blue-600 border-blue-500/30">
                  LIVE
                </Badge>
              )}
              {isConnected ? (
                <Badge variant="success" className="bg-green-500/10 text-green-600 border-green-500/30">
                  <Check className="w-3 h-3 mr-1" /> Connected
                </Badge>
              ) : (
                <Badge variant="secondary" className="text-muted-foreground">
                  <X className="w-3 h-3 mr-1" /> Disconnected
                </Badge>
              )}
            </div>
            
            <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
              {integration.description}
            </p>
            
            {integration.companyName && (
              <p className="text-xs text-green-600 mb-2">
                {integration.companyName}
              </p>
            )}
            
            <div className="flex flex-wrap gap-1 mb-3">
              {integration.entities.map((entity) => (
                <Badge key={entity} variant="outline" className="text-xs">
                  {entity}
                </Badge>
              ))}
            </div>
            
            <div className="flex items-center gap-2">
              {isConnected ? (
                <>
                  {isReal && (
                    <Button
                      size="sm"
                      onClick={() => integration.authType === 'cookie_import' ? onConnect(integration) : onViewDetails(integration)}
                      data-testid={`view-${integration.id}`}
                    >
                      {integration.authType === 'cookie_import' ? (
                        <><Play className="w-3 h-3 mr-1" /> Scrape Now</>
                      ) : (
                        <><Download className="w-3 h-3 mr-1" /> {integration.id === 'pennylane' || integration.id === 'wise' || integration.id === 'xero' ? 'Sync Data' : 'View Data'}</>
                      )}
                    </Button>
                  )}
                  {integration.authType !== 'cookie_import' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onConfigure(integration)}
                      data-testid={`configure-${integration.id}`}
                    >
                      <Settings2 className="w-3 h-3 mr-1" /> Configure
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDisconnect(integration)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`disconnect-${integration.id}`}
                  >
                    {integration.authType === 'cookie_import' ? 'Re-import Cookies' : 'Disconnect'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={() => onConnect(integration)}
                    data-testid={`connect-${integration.id}`}
                  >
                    Connect
                  </Button>
                  {integration.authType === 'xero_oauth' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onConfigure(integration)}
                      data-testid={`configure-${integration.id}`}
                    >
                      <Settings2 className="w-3 h-3 mr-1" /> Configure
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Connection dialog for OAuth integrations (MOCK)
function OAuthConnectDialog({ integration, open, onClose }) {
  const [isConnecting, setIsConnecting] = useState(false);
  
  const handleConnect = () => {
    setIsConnecting(true);
    setTimeout(() => {
      setIsConnecting(false);
      toast.success(`${integration?.name} connected successfully!`);
      onClose(true);
    }, 2000);
  };
  
  if (!integration) return null;
  
  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect {integration.name}</DialogTitle>
          <DialogDescription>
            You'll be redirected to {integration.name} to authorize access.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This integration is not yet available. OAuth flow will redirect to {integration.name}.
            </AlertDescription>
          </Alert>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>
            Cancel
          </Button>
          <Button onClick={handleConnect} disabled={isConnecting}>
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Connecting...
              </>
            ) : (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                Connect (Mock)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Connection dialog for generic API Key integrations (stub)
function ApiKeyConnectDialog({ integration, open, onClose }) {
  if (!integration) return null;
  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect {integration.name}</DialogTitle>
          <DialogDescription>
            API key integration for {integration.name}.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This integration is not yet available.
            </AlertDescription>
          </Alert>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Connection dialog for Pennylane API key (real, per-entity)
function PennylaneConnectDialog({ entity, open, onClose, onSuccess }) {
  const [apiKey, setApiKey] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState('');

  const handleConnect = async () => {
    if (!apiKey.trim()) {
      toast.error('Please enter an API key');
      return;
    }
    if (!entity) return;

    setIsConnecting(true);
    setError('');
    try {
      const result = await pennylaneAPI.connect(entity.entity_id, apiKey);
      const d = result.data;
      toast.success(`${entity.canonical_name} connected (${d.this_key_controls})`);
      setApiKey('');
      onSuccess?.();
      onClose(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'object'
        ? (detail.error || 'Connection failed')
        : (detail || getErrorMessage(err));
      setError(msg);
      toast.error(msg);
    } finally {
      setIsConnecting(false);
    }
  };

  if (!entity) return null;

  return (
    <Dialog open={open} onOpenChange={() => { setApiKey(''); setError(''); onClose(false); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Connect {entity.canonical_name} to Pennylane</DialogTitle>
          <DialogDescription>
            Enter the Pennylane API key for {entity.canonical_name}. The key will be validated
            against Pennylane and stored encrypted.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="pennylane-api-key">API Key</Label>
            <Input
              id="pennylane-api-key"
              type="password"
              placeholder="plk_live_..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              data-testid="pennylane-api-key-input"
              autoComplete="off"
            />
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => { setApiKey(''); setError(''); onClose(false); }}>
            Cancel
          </Button>
          <Button
            onClick={handleConnect}
            disabled={isConnecting || !apiKey.trim()}
            data-testid="pennylane-connect-submit"
          >
            {isConnecting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Validating...
              </>
            ) : (
              'Connect'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Xero client credentials dialog (Configure surface for client_id/client_secret)
function XeroCredentialsDialog({ open, onClose, onSuccess }) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);

  const handleSave = async () => {
    if (!clientId.trim() || !clientSecret.trim()) {
      setError('Both Client ID and Client Secret are required');
      return;
    }
    setIsSaving(true);
    setError('');
    setResult(null);
    try {
      const res = await xeroAPI.saveCredentials(clientId.trim(), clientSecret.trim(), confirmOverwrite);
      setResult(res.data);
      toast.success('Xero client credentials saved (encrypted)');
      setClientId('');
      setClientSecret('');
      setConfirmOverwrite(false);
      onSuccess?.();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409) {
        setConfirmOverwrite(true);
        setError(typeof detail === 'string' ? detail : 'Credentials already exist. Click Save again to overwrite.');
      } else {
        setError(typeof detail === 'string' ? detail : getErrorMessage(err));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setClientId('');
    setClientSecret('');
    setError('');
    setResult(null);
    setConfirmOverwrite(false);
    onClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Configure Xero Credentials</DialogTitle>
          <DialogDescription>
            Enter the Client ID and Client Secret from your Xero developer app.
            Credentials will be stored encrypted in the database.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="xero-client-id">Client ID</Label>
            <Input
              id="xero-client-id"
              type="text"
              placeholder="Xero app Client ID"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              data-testid="xero-client-id-input"
              autoComplete="off"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="xero-client-secret">Client Secret</Label>
            <Input
              id="xero-client-secret"
              type="password"
              placeholder="Xero app Client Secret"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              data-testid="xero-client-secret-input"
              autoComplete="off"
            />
          </div>

          {error && (
            <Alert variant={confirmOverwrite ? 'default' : 'destructive'}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {result && (
            <Alert>
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription>
                Client ID: {result.client_id_present ? 'stored' : 'missing'},
                Client Secret: {result.client_secret_present ? 'stored' : 'missing'}
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving || !clientId.trim() || !clientSecret.trim()}
            data-testid="xero-credentials-submit"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : confirmOverwrite ? (
              'Overwrite and Save'
            ) : (
              'Save'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Cookie import dialog for CWC Smoobu browser scraper
function CookieImportDialog({ open, onClose, onImport, scraperStatus }) {
  const [cookiesText, setCookiesText] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const handleImport = async () => {
    if (!cookiesText.trim()) {
      toast.error('Please paste your cookies');
      return;
    }
    setIsImporting(true);
    try {
      await onImport(cookiesText);
      setCookiesText('');
    } finally {
      setIsImporting(false);
    }
  };

  const snippet = `copy(document.cookie.split(';').map(c => { const [n,...r] = c.trim().split('='); return {name:n, value:r.join('='), domain:'.smoobu.com', path:'/'}; }));`;

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Import Smoobu Cookies (CWC)</DialogTitle>
          <DialogDescription>
            Import browser cookies to enable scraping of Cozy With Character's Smoobu dashboard.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          {scraperStatus?.has_session && !scraperStatus?.session_valid && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Your previous session has expired. Please re-import cookies.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label className="text-sm font-medium">Step 1: Copy cookies from Smoobu</Label>
            <p className="text-xs text-muted-foreground">
              Log into <a href="https://login.smoobu.com" target="_blank" rel="noreferrer" className="underline">login.smoobu.com</a> with
              the CWC account, open DevTools (F12), go to Console, and paste this snippet:
            </p>
            <div className="relative">
              <pre className="text-xs bg-muted p-2 rounded overflow-x-auto whitespace-pre-wrap break-all">
                {snippet}
              </pre>
              <Button
                variant="ghost"
                size="sm"
                className="absolute top-1 right-1 h-6 w-6 p-0"
                onClick={() => { navigator.clipboard.writeText(snippet); toast.success('Snippet copied'); }}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              This copies your cookies to clipboard. Then paste below.
            </p>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Step 2: Paste cookies</Label>
            <Textarea
              placeholder='[{"name":"session_id","value":"abc123","domain":".smoobu.com","path":"/"}]'
              rows={5}
              value={cookiesText}
              onChange={(e) => setCookiesText(e.target.value)}
              className="font-mono text-xs"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose()}>Cancel</Button>
          <Button onClick={handleImport} disabled={isImporting || !cookiesText.trim()}>
            {isImporting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Key className="w-4 h-4 mr-2" />
                Import Cookies
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Permission labels for display ────────────────────────────────────
const PERMISSION_GROUPS = {
  'Read': [
    { value: 'read:entities', label: 'Entities' },
    { value: 'read:transactions', label: 'Transactions' },
    { value: 'read:invoices', label: 'Invoices' },
    { value: 'read:bank-accounts', label: 'Bank Accounts' },
    { value: 'read:dashboard', label: 'Dashboard' },
    { value: 'read:reports', label: 'Reports' },
    { value: 'read:alerts', label: 'Alerts' },
    { value: 'read:subscriptions', label: 'Subscriptions' },
    { value: 'read:wealth', label: 'Wealth' },
    { value: 'read:intercompany', label: 'Intercompany' },
    { value: 'read:tax-reserves', label: 'Tax Reserves' },
    { value: 'read:exchange-rates', label: 'Exchange Rates' },
    { value: 'read:cashflow', label: 'Cash Forecast' },
  ],
  'Write': [
    { value: 'write:transactions', label: 'Transactions' },
    { value: 'write:invoices', label: 'Invoices' },
    { value: 'write:alerts', label: 'Alerts' },
    { value: 'write:matching', label: 'Matching' },
  ],
};

const METHOD_COLORS = {
  GET: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
  POST: 'bg-green-500/10 text-green-600 border-green-500/30',
  PUT: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  DELETE: 'bg-red-500/10 text-red-600 border-red-500/30',
  PATCH: 'bg-purple-500/10 text-purple-600 border-purple-500/30',
};

function APIKeysTab() {
  const [apiKeys, setApiKeys] = useState([]);
  const [apiDocs, setApiDocs] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState([
    'read:entities', 'read:transactions', 'read:invoices', 'read:dashboard', 'read:reports',
  ]);
  const [newKeyExpiry, setNewKeyExpiry] = useState('none');
  const [isCreating, setIsCreating] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copiedKey, setCopiedKey] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showDocs, setShowDocs] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [keysRes, docsRes] = await Promise.allSettled([
        apiKeysAPI.getAll(),
        apiDocsAPI.getEndpoints(),
      ]);
      if (keysRes.status === 'fulfilled') setApiKeys(keysRes.value.data);
      if (docsRes.status === 'fulfilled') setApiDocs(docsRes.value.data);
    } catch (error) {
      console.error('Error fetching API keys data:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!newKeyName.trim()) return;
    setIsCreating(true);
    try {
      const expiryMap = { none: null, '30': 30, '90': 90, '365': 365 };
      const result = await apiKeysAPI.create({
        name: newKeyName,
        permissions: newKeyPermissions,
        expires_in_days: expiryMap[newKeyExpiry],
      });
      setCreatedKey(result.data);
      toast.success('API key created successfully');
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsCreating(false);
    }
  };

  const handleRevoke = async (keyId, keyName) => {
    try {
      await apiKeysAPI.delete(keyId);
      toast.success(`API key "${keyName}" revoked`);
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleToggleActive = async (key) => {
    try {
      await apiKeysAPI.update(key.id, { is_active: !key.is_active });
      toast.success(`API key ${key.is_active ? 'disabled' : 'enabled'}`);
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCopyKey = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopiedKey(true);
    setTimeout(() => setCopiedKey(false), 2000);
  };

  const togglePermission = (perm) => {
    setNewKeyPermissions(prev =>
      prev.includes(perm)
        ? prev.filter(p => p !== perm)
        : [...prev, perm]
    );
  };

  const selectAllPermissions = (group) => {
    const groupPerms = PERMISSION_GROUPS[group].map(p => p.value);
    const allSelected = groupPerms.every(p => newKeyPermissions.includes(p));
    if (allSelected) {
      setNewKeyPermissions(prev => prev.filter(p => !groupPerms.includes(p)));
    } else {
      setNewKeyPermissions(prev => [...new Set([...prev, ...groupPerms])]);
    }
  };

  const toggleCategory = (name) => {
    setExpandedCategories(prev => ({ ...prev, [name]: !prev[name] }));
  };

  const resetCreateForm = () => {
    setNewKeyName('');
    setNewKeyPermissions(['read:entities', 'read:transactions', 'read:invoices', 'read:dashboard', 'read:reports']);
    setNewKeyExpiry('none');
    setCreatedKey(null);
    setCopiedKey(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* API Keys Management */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Key className="w-5 h-5" />
                API Keys
              </CardTitle>
              <CardDescription>
                Create and manage API keys for programmatic access to the FinanceAI API.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowDocs(!showDocs)}
              >
                <BookOpen className="w-4 h-4 mr-2" />
                {showDocs ? 'Hide' : 'Show'} API Docs
              </Button>
              <Button
                size="sm"
                onClick={() => { resetCreateForm(); setShowCreateDialog(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Key
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {apiKeys.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Key className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No API keys yet</p>
              <p className="text-sm mt-1">Create an API key to allow external programs to access your data.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Permissions</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apiKeys.map((key) => (
                  <TableRow key={key.id}>
                    <TableCell className="font-medium">{key.name}</TableCell>
                    <TableCell>
                      <code className="text-xs bg-muted px-2 py-1 rounded font-mono">
                        {key.masked_key || key.key_prefix + '...'}
                      </code>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(key.permissions || []).slice(0, 3).map(p => (
                          <Badge key={p} variant="outline" className="text-xs">{p}</Badge>
                        ))}
                        {(key.permissions || []).length > 3 && (
                          <Badge variant="outline" className="text-xs">+{key.permissions.length - 3}</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {key.created_at ? new Date(key.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {key.last_used_at ? new Date(key.last_used_at).toLocaleDateString() : 'Never'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={key.is_active
                          ? 'bg-green-500/10 text-green-600 border-green-500/30'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400'
                        }
                        variant="outline"
                      >
                        {key.is_active ? 'Active' : 'Disabled'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(key)}
                          title={key.is_active ? 'Disable' : 'Enable'}
                        >
                          <Shield className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleRevoke(key.id, key.name)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* API Documentation */}
      {showDocs && apiDocs && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              API Documentation
            </CardTitle>
            <CardDescription>
              Available API endpoints for programmatic access. All requests require an API key.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Authentication info */}
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <h4 className="font-medium text-sm">Authentication</h4>
              <p className="text-sm text-muted-foreground">
                Include your API key in the <code className="bg-muted px-1.5 py-0.5 rounded text-xs font-mono">X-API-Key</code> header with every request.
              </p>
              <div className="bg-background rounded border p-3 font-mono text-xs">
                <span className="text-muted-foreground">curl</span> {apiDocs.base_url}/entities \<br />
                {'  '}<span className="text-muted-foreground">-H</span> <span className="text-green-600">"X-API-Key: fai_your_key_here"</span>
              </div>
            </div>

            {/* Endpoint categories */}
            <div className="space-y-2">
              {apiDocs.categories?.map((category) => (
                <div key={category.name} className="border rounded-lg">
                  <button
                    className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors text-left"
                    onClick={() => toggleCategory(category.name)}
                  >
                    <div className="flex items-center gap-2">
                      {expandedCategories[category.name]
                        ? <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        : <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      }
                      <span className="font-medium text-sm">{category.name}</span>
                      <Badge variant="outline" className="text-xs">{category.endpoints?.length || 0}</Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">{category.permission}</span>
                  </button>

                  {expandedCategories[category.name] && (
                    <div className="border-t px-3 pb-3">
                      <p className="text-xs text-muted-foreground py-2">{category.description}</p>
                      <div className="space-y-1.5">
                        {category.endpoints?.map((ep, i) => (
                          <div key={i} className="flex items-start gap-2 py-1.5 px-2 rounded hover:bg-muted/30">
                            <Badge
                              variant="outline"
                              className={`text-xs font-mono shrink-0 ${METHOD_COLORS[ep.method] || ''}`}
                            >
                              {ep.method}
                            </Badge>
                            <code className="text-xs font-mono text-foreground shrink-0">{ep.path}</code>
                            <span className="text-xs text-muted-foreground">{ep.description}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create API Key Dialog */}
      <Dialog
        open={showCreateDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowCreateDialog(false);
            setCreatedKey(null);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          {createdKey ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  API Key Created
                </DialogTitle>
                <DialogDescription>
                  Copy your API key now. You won't be able to see it again.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <Alert className="border-amber-500/30 bg-amber-500/5">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <AlertDescription className="text-amber-700 dark:text-amber-400">
                    Make sure to copy your API key now. It will only be shown once.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label>API Key</Label>
                  <div className="flex gap-2">
                    <Input
                      readOnly
                      value={createdKey.key}
                      className="font-mono text-xs"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCopyKey(createdKey.key)}
                    >
                      {copiedKey ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>
                <div className="text-sm space-y-1">
                  <p><span className="text-muted-foreground">Name:</span> {createdKey.name}</p>
                  <p><span className="text-muted-foreground">Permissions:</span> {createdKey.permissions?.length} scope(s)</p>
                  <p><span className="text-muted-foreground">Expires:</span> {createdKey.expires_at ? new Date(createdKey.expires_at).toLocaleDateString() : 'Never'}</p>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={() => { setShowCreateDialog(false); setCreatedKey(null); }}>Done</Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Create API Key</DialogTitle>
                <DialogDescription>
                  Create a new API key for external program access (e.g. Claude Code Chief of Staff).
                </DialogDescription>
              </DialogHeader>
              <div className="py-4 space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="key-name">Key Name</Label>
                  <Input
                    id="key-name"
                    placeholder="e.g. Chief of Staff Agent"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Permissions</Label>
                  <div className="border rounded-lg p-3 max-h-48 overflow-y-auto space-y-3">
                    {Object.entries(PERMISSION_GROUPS).map(([group, perms]) => (
                      <div key={group}>
                        <div className="flex items-center gap-2 mb-1.5">
                          <button
                            className="text-xs font-medium text-primary hover:underline"
                            onClick={() => selectAllPermissions(group)}
                          >
                            {group} ({perms.filter(p => newKeyPermissions.includes(p.value)).length}/{perms.length})
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-1">
                          {perms.map((perm) => (
                            <label
                              key={perm.value}
                              className="flex items-center gap-2 text-xs py-1 px-1.5 rounded hover:bg-muted/50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={newKeyPermissions.includes(perm.value)}
                                onChange={() => togglePermission(perm.value)}
                                className="rounded border-gray-300"
                              />
                              {perm.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Expiration</Label>
                  <Select value={newKeyExpiry} onValueChange={setNewKeyExpiry}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No expiration</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                      <SelectItem value="90">90 days</SelectItem>
                      <SelectItem value="365">1 year</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
                <Button
                  onClick={handleCreate}
                  disabled={isCreating || !newKeyName.trim() || newKeyPermissions.length === 0}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create Key'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Open Banking connections list (Integrations -> Open Banking -> View
// Details). This is the surface the operator manages an already-connected
// institution from -- "Change provider" belongs here, on a connected row,
// not buried in the "Connect bank account" add-new dialog.
export function ObConnectionsPanel({ obStatus, obSyncing, onSync, onClose, onChangeProvider }) {
  const [adoptionOpenForConnection, setAdoptionOpenForConnection] = useState(null);
  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold">Open Banking Connections</h2>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={obSyncing} onClick={onSync}>
            {obSyncing ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Syncing...</> : <><RefreshCw className="w-3 h-3 mr-1" /> Sync Now</>}
          </Button>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-4 h-4 mr-1" /> Close
          </Button>
        </div>
      </div>
      <div className="grid gap-3">
        {(obStatus.connections || []).map((conn) => (
          <Card key={conn.connection_id} className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-2.5 h-2.5 rounded-full ${conn.connected ? 'bg-green-500' : 'bg-gray-300'}`} />
                <div>
                  <p className="font-medium">{conn.institution_name || conn.label || conn.connection_id}</p>
                  <p className="text-xs text-muted-foreground">
                    {conn.account_count || 0} account(s)
                    {conn.consent_status === 'active' && ' · consent active'}
                    {conn.consent_status === 'expiring_soon' && ' · consent expiring soon'}
                    {conn.consent_status === 'expired' && ' · consent expired'}
                  </p>
                  {conn.last_synced && (
                    <p className="text-xs text-muted-foreground">
                      Last synced: {new Date(conn.last_synced).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                  {conn.connected_at && (
                    <p className="text-xs text-muted-foreground">
                      Connected: {new Date(conn.connected_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  )}
                </div>
              </div>
              {conn.connected && (
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`ob-adopt-accounts-${conn.connection_id}`}
                    onClick={() => setAdoptionOpenForConnection(
                      adoptionOpenForConnection === conn.connection_id ? null : conn.connection_id,
                    )}
                  >
                    Adopt accounts
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid={`ob-change-provider-${conn.connection_id}`}
                    onClick={() => onChangeProvider(conn)}
                  >
                    Change provider
                  </Button>
                </div>
              )}
            </div>
            {adoptionOpenForConnection === conn.connection_id && (
              <div className="mt-3 pt-3 border-t">
                <AccountAdoptionPanel connectionId={conn.connection_id} />
              </div>
            )}
          </Card>
        ))}
        {(!obStatus.connections || obStatus.connections.length === 0) && (
          <p className="text-sm text-muted-foreground">No open banking connections. Click "Add Connection" to connect a bank.</p>
        )}
      </div>
    </div>
  );
}

export function IntegrationsPage() {
  const [pennylaneStatus, setPennylaneStatus] = useState(null);
  const [pennylaneEntityStatus, setPennylaneEntityStatus] = useState(null);
  const [pennylaneSyncHistory, setPennylaneSyncHistory] = useState([]);
  const [pennylaneInvoices, setPennylaneInvoices] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [schedulerConfig, setSchedulerConfig] = useState(null);
  const [wiseStatus, setWiseStatus] = useState(null);
  const [revolutStatus, setRevolutStatus] = useState(null);
  const [xeroStatus, setXeroStatus] = useState(null);
  const [googleAccounts, setGoogleAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uplistingStatus, setUplistingStatus] = useState(null);
  const [brosStatus, setBrosStatus] = useState(null);
  const [smoobuStatus, setSmoobuStatus] = useState(null);
  const [hostawayStatus, setHostawayStatus] = useState(null);
  const [scraperStatus, setScraperStatus] = useState(null);
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [obStatus, setObStatus] = useState(null);
  const [obInstitutions, setObInstitutions] = useState([]);
  const [showObInstitutionPicker, setShowObInstitutionPicker] = useState(false);
  // Ambiguous-provider picker (Commit 3): set when connect/reconnect returns
  // requires_picker=true. Null when no picker is pending.
  const [obProviderPicker, setObProviderPicker] = useState(null);
  const [obProviderPickerBusy, setObProviderPickerBusy] = useState(false);
  const [showObDetails, setShowObDetails] = useState(false);
  const [obSyncing, setObSyncing] = useState(false);
  const [ebStatus, setEbStatus] = useState(null);
  const [ebInstitutions, setEbInstitutions] = useState([]);
  const [showEbInstitutionPicker, setShowEbInstitutionPicker] = useState(false);
  const [showEbDetails, setShowEbDetails] = useState(false);
  const [ebSyncing, setEbSyncing] = useState(false);
  const [ebSyncProgress, setEbSyncProgress] = useState(null); // {synced, failed, total_accounts, status, account_outcomes}
  const [ebTxSyncing, setEbTxSyncing] = useState(false);
  const [ebTxSyncProgress, setEbTxSyncProgress] = useState(null);
  const [ebRelinkingId, setEbRelinkingId] = useState(null);
  const [ebRetryingId, setEbRetryingId] = useState(null);
  const ebSectionRef = useRef(null);
  const [gcStatus, setGcStatus] = useState(null);
  const [gcInstitutions, setGcInstitutions] = useState([]);
  const [gcInstitutionFilter, setGcInstitutionFilter] = useState('');
  const [showGcCredentialsForm, setShowGcCredentialsForm] = useState(false);
  const [gcSecretId, setGcSecretId] = useState('');
  const [gcSecretKey, setGcSecretKey] = useState('');
  const [gcSavingCredentials, setGcSavingCredentials] = useState(false);
  const [gcCredentialsStatus, setGcCredentialsStatus] = useState(null);
  const [showGcInstitutionPicker, setShowGcInstitutionPicker] = useState(false);
  const [showGcDetails, setShowGcDetails] = useState(false);
  const [gcSyncingId, setGcSyncingId] = useState(null);
  const [gcSyncProgress, setGcSyncProgress] = useState(null);
  const [showPennylaneDetails, setShowPennylaneDetails] = useState(false);
  const [showPennylaneConnect, setShowPennylaneConnect] = useState(false);
  const [pennylaneConnectEntity, setPennylaneConnectEntity] = useState(null);
  const [showXeroDetails, setShowXeroDetails] = useState(false);
  const [showXeroCredentials, setShowXeroCredentials] = useState(false);
  const [showRevolutDetails, setShowRevolutDetails] = useState(false);
  const [showUplistingDetails, setShowUplistingDetails] = useState(false);
  const [showSmoobuDetails, setShowSmoobuDetails] = useState(false);
  const [showHostawayDetails, setShowHostawayDetails] = useState(false);
  const [showCookieImport, setShowCookieImport] = useState(false);
  const uplistingDashboardRef = useRef(null);
  const smoobuDashboardRef = useRef(null);
  const hostawayDashboardRef = useRef(null);

  const [integrations, setIntegrations] = useState({});
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [dialogType, setDialogType] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  
  // Fetch all data on mount
  const fetchAllData = useCallback(async () => {
    try {
      const [statusRes, historyRes, invoicesRes, schedulerStatusRes, schedulerConfigRes, googleRes, wiseRes, revolutRes, xeroRes, uplistingRes, brosRes, smoobuRes, hostawayRes, scraperRes, plEntitiesRes, bridgeRes, obRes, obInstRes, ebRes, ebInstRes, gcRes, gcInstRes] = await Promise.allSettled([
        pennylaneAPI.getStatus(),
        pennylaneAPI.getSyncHistory(),
        pennylaneAPI.getInvoices('all', 100),
        schedulerAPI.getStatus(),
        schedulerAPI.getConfig(),
        googleAPI.getAccounts(),
        wiseAPI.getStatus(),
        revolutAPI.getStatus(),
        xeroAPI.getStatus(),
        uplistingAPI.test(),
        brosAPI.test(),
        smoobuAPI.test(),
        hostawayAPI.test(),
        smoobuAPI.getScraperStatus(),
        pennylaneAPI.getEntities(),
        bridgeAPI.getStatus(),
        openbankingAPI.getStatus(),
        openbankingAPI.getInstitutions(),
        enablebankingAPI.getStatus(),
        enablebankingAPI.getInstitutions(),
        gocardlessAPI.getStatus(),
        gocardlessAPI.getInstitutions(),
      ]);

      const plStatus = statusRes.status === 'fulfilled' ? statusRes.value.data : null;
      const plHistory = historyRes.status === 'fulfilled' ? (historyRes.value.data.history || []) : [];
      const plInvoices = invoicesRes.status === 'fulfilled' ? (invoicesRes.value.data.invoices || []) : [];
      const schedStatus = schedulerStatusRes.status === 'fulfilled' ? schedulerStatusRes.value.data : null;
      const schedConfig = schedulerConfigRes.status === 'fulfilled' ? schedulerConfigRes.value.data : null;
      const gAccounts = googleRes.status === 'fulfilled' ? (googleRes.value.data.accounts || []) : [];
      const wStatus = wiseRes.status === 'fulfilled' ? wiseRes.value.data : null;
      const revStatus = revolutRes.status === 'fulfilled' ? revolutRes.value.data : null;
      const xStatus = xeroRes.status === 'fulfilled' ? xeroRes.value.data : null;
      const ulStatus = uplistingRes.status === 'fulfilled' ? uplistingRes.value.data : null;
      const brStatus = brosRes.status === 'fulfilled' ? brosRes.value.data : null;
      const smStatus = smoobuRes.status === 'fulfilled' ? smoobuRes.value.data : null;
      const hwStatus = hostawayRes.status === 'fulfilled' ? hostawayRes.value.data : null;
      const scStatus = scraperRes.status === 'fulfilled' ? scraperRes.value.data : null;
      const plEntityData = plEntitiesRes.status === 'fulfilled' ? plEntitiesRes.value.data : null;
      const bgStatus = bridgeRes.status === 'fulfilled' ? bridgeRes.value.data : null;
      const obSt = obRes.status === 'fulfilled' ? obRes.value.data : null;
      const obInst = obInstRes.status === 'fulfilled' ? (obInstRes.value.data.institutions || []) : [];
      const ebSt = ebRes.status === 'fulfilled' ? ebRes.value.data : null;
      const ebInst = ebInstRes.status === 'fulfilled' ? (ebInstRes.value.data.institutions || []) : [];
      const gcSt = gcRes.status === 'fulfilled' ? gcRes.value.data : null;
      const gcInst = gcInstRes.status === 'fulfilled' ? (gcInstRes.value.data.institutions || []) : [];

      setPennylaneStatus(plStatus);
      setPennylaneSyncHistory(plHistory);
      setPennylaneInvoices(plInvoices);
      setPennylaneEntityStatus(plEntityData);
      setSchedulerStatus(schedStatus);
      setSchedulerConfig(schedConfig);
      setGoogleAccounts(gAccounts);
      setWiseStatus(wStatus);
      setRevolutStatus(revStatus);
      setXeroStatus(xStatus);
      setUplistingStatus(ulStatus);
      setBrosStatus(brStatus);
      setSmoobuStatus(smStatus);
      setHostawayStatus(hwStatus);
      setScraperStatus(scStatus);
      setBridgeStatus(bgStatus);
      setObStatus(obSt);
      setObInstitutions(obInst);
      setEbStatus(ebSt);
      setEbInstitutions(ebInst);
      setGcStatus(gcSt);
      setGcInstitutions(gcInst);

      setIntegrations(getIntegrationCategories(plStatus, gAccounts, wStatus, revStatus, xStatus, ulStatus, brStatus, smStatus, hwStatus, scStatus, plEntityData, bgStatus, obSt, ebSt, gcSt));
    } catch (error) {
      console.error('Error fetching data:', error);
      setIntegrations(getIntegrationCategories(null, [], null, null, null, null, null, null, null, null, null, null, null, null, null));
    } finally {
      setIsLoading(false);
    }
  }, []);
  
  // Check URL params for Google OAuth callback
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const googleConnected = params.get('google_connected');
    const googleError = params.get('google_error');
    
    const revolutConnected = params.get('revolut_connected');
    const revolutError = params.get('revolut_error');
    const xeroConnected = params.get('xero_connected');
    const xeroError = params.get('xero_error');
    const bridgeConnected = params.get('bridge_connected');
    const bridgeError = params.get('bridge_error');
    const obConnected = params.get('ob_connected');
    const obError = params.get('ob_error');
    const gcConnected = params.get('gc_connected');
    const gcError = params.get('gc_error');

    if (googleConnected) {
      toast.success(`Google account connected successfully!`);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      fetchAllData();
    }
    if (googleError) {
      toast.error('Google connection failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (revolutConnected) {
      toast.success(`Revolut Business (${revolutConnected}) connected successfully!`);
      window.history.replaceState({}, '', window.location.pathname);
      fetchAllData();
    }
    if (revolutError) {
      toast.error('Revolut connection failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (xeroConnected) {
      toast.success('Xero connected successfully!');
      window.history.replaceState({}, '', window.location.pathname);
      fetchAllData();
    }
    if (xeroError) {
      toast.error('Xero connection failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (bridgeConnected) {
      toast.success(`Bridge (BNP) connection established! Accounts will appear in Entities Admin as UNASSIGNED.`);
      window.history.replaceState({}, '', window.location.pathname);
      fetchAllData();
    }
    if (bridgeError) {
      toast.error('Bridge (BNP) connection failed. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (obConnected) {
      toast.success('Open banking connection established! Accounts will appear in Entities Admin as UNASSIGNED.');
      window.history.replaceState({}, '', window.location.pathname);
      fetchAllData();
    }
    if (obError) {
      toast.error(`Open banking connection failed: ${obError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (gcConnected) {
      toast.success('GoCardless connection established! Accounts will appear in Entities Admin as UNASSIGNED.');
      window.history.replaceState({}, '', window.location.pathname);
      fetchAllData();
    }
    if (gcError) {
      toast.error(`GoCardless connection failed: ${gcError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchAllData]);
  
  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  // Deep-link from the dashboard's stale-feeds warning (GroupOverviewTab's
  // FreshnessBadge, ?scrollTo=enablebanking) straight to the Enable Banking
  // card instead of a bare /integrations landing the operator has to hunt
  // through. Waits on ebStatus so the panel exists to scroll to.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('scrollTo') === 'enablebanking' && ebStatus) {
      setShowEbDetails(true);
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(() => ebSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [ebStatus]);
  
  const handleSync = async () => {
    try {
      const result = await pennylaneAPI.sync();
      const entityCount = (result.data.entities_synced || []).length;
      const entitySuffix = entityCount ? ` across ${entityCount} entit${entityCount === 1 ? 'y' : 'ies'}` : '';
      toast.success(`Synced ${result.data.invoices_synced} invoices and ${result.data.expenses_synced} expenses${entitySuffix}`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  
  const handleUpdateScheduler = async (config) => {
    await schedulerAPI.updateConfig(config);
    // Refresh scheduler data
    const [statusRes, configRes] = await Promise.all([
      schedulerAPI.getStatus(),
      schedulerAPI.getConfig()
    ]);
    setSchedulerStatus(statusRes.data);
    setSchedulerConfig(configRes.data);
  };
  
  // Google OAuth handlers
  const handleGoogleConnect = async (account) => {
    try {
      const response = await googleAPI.initiateAuth(account.accountId);
      // Redirect to Google OAuth (validate URL origin)
      const authUrl = new URL(response.data.auth_url);
      if (!authUrl.hostname.endsWith('google.com') && !authUrl.hostname.endsWith('googleapis.com')) {
        throw new Error('Invalid OAuth URL');
      }
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  
  const handleGoogleDisconnect = async (account) => {
    try {
      await googleAPI.disconnect(account.accountId);
      toast.success(`Disconnected ${account.email}`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };
  
  const refreshGoogleAccounts = async () => {
    try {
      const { data } = await googleAPI.getAccounts();
      setGoogleAccounts(data.accounts || []);
    } catch {
      // Best-effort refresh; the next full fetchAllData() will catch up.
    }
  };

  const SCAN_JOB_POLL_INTERVAL_MS = 2000;
  const SCAN_JOB_POLL_TIMEOUT_MS = 10 * 60 * 1000; // 10min, generous vs a 200-message scan

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // process-invoices now returns 202 + a job id (single-flighted per
  // account, runs in the background) instead of blocking the request --
  // see routes/google_integrations.py::process_and_file_invoices. Polls
  // job-status until the job reaches a terminal state.
  const pollScanJob = async (accountId, jobId) => {
    const deadline = Date.now() + SCAN_JOB_POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
      const { data: job } = await googleAPI.processInvoicesJobStatus(accountId, jobId);
      if (job.status === 'completed' || job.status === 'failed') {
        return job;
      }
      await sleep(SCAN_JOB_POLL_INTERVAL_MS);
    }
    throw new Error(`Scan for ${accountId} timed out waiting for job ${jobId} to finish`);
  };

  // The pre-flight HTTPException shape (reauth required / API not
  // configured / scan failed) now lands as job.result.http_error instead
  // of a live axios error response, once the scan runs as a background
  // job -- rebuild the axios-error shape isGoogleReauthRequiredError/
  // isGoogleApiNotConfiguredError already know how to read, rather than
  // duplicating their status/reason parsing here.
  const asAxiosErrorShape = (httpError) => ({
    response: { status: httpError.status_code, data: { detail: httpError.detail } },
  });

  const toastScanJobOutcome = (job) => {
    if (job.status === 'failed') {
      toast.error(job.error || 'Gmail scan failed');
      return;
    }
    const result = job.result || {};
    if (result.http_error) {
      const err = asAxiosErrorShape(result.http_error);
      if (isGoogleReauthRequiredError(err)) {
        toast.error(getGoogleReauthMessage(err));
      } else if (isGoogleApiNotConfiguredError(err)) {
        toast.error(getGoogleApiNotConfiguredMessage(err));
      } else {
        toast.error(result.http_error.detail?.message || 'Gmail scan failed');
      }
      return;
    }
    if (result.processed > 0) {
      toast.success(`Processed ${result.processed} invoice(s) from Gmail`);
    } else {
      toast.info('No new invoices found in Gmail');
    }
  };

  const handleScanInvoices = async (accountId) => {
    try {
      toast.info('Scanning Gmail for invoices...');
      const { data: started } = await googleAPI.processInvoices(accountId);
      const job = await pollScanJob(accountId, started.job_id);
      toastScanJobOutcome(job);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      await refreshGoogleAccounts();
    }
  };

  const [isScanningAll, setIsScanningAll] = useState(false);

  const handleScanAllInvoices = async () => {
    setIsScanningAll(true);
    try {
      toast.info('Scanning all Gmail accounts for invoices...');
      const { data: started } = await googleAPI.processAllInvoices();
      const jobs = await Promise.all(
        (started.jobs || []).map((j) => pollScanJob(j.account_id, j.job_id).catch((err) => ({
          status: 'failed', error: err.message, account_email: j.account_email,
        })))
      );

      const failedAccounts = [];
      let totalProcessed = 0;
      let accountsSucceeded = 0;
      for (const [i, job] of jobs.entries()) {
        const accountEmail = started.jobs[i]?.account_email || started.jobs[i]?.account_id;
        const result = job.result || {};
        if (job.status === 'failed' || result.http_error) {
          failedAccounts.push(accountEmail);
        } else {
          accountsSucceeded += 1;
          totalProcessed += result.processed || 0;
        }
      }

      if (failedAccounts.length > 0) {
        toast.error(`Failed to scan: ${failedAccounts.join(', ')}. Reconnect in settings below.`);
      }
      if (totalProcessed > 0) {
        toast.success(`Processed ${totalProcessed} invoice(s) from ${accountsSucceeded}/${started.accounts_processed} account(s)`);
      } else if (failedAccounts.length === 0) {
        toast.info(`Scanned ${started.accounts_processed} account(s) — no new invoices found`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setIsScanningAll(false);
      await refreshGoogleAccounts();
    }
  };
  
  const handleWiseSync = async () => {
    try {
      toast.info('Syncing Wise accounts and transactions...');
      const result = await wiseAPI.sync();
      const d = result.data;
      toast.success(`Wise synced: ${d.accounts_synced} accounts, ${d.transactions_synced} transactions`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRevolutConnect = async (connectionId, opts) => {
    try {
      const response = await revolutAPI.initiateAuth(connectionId, opts);
      const revolutUrl = new URL(response.data.auth_url);
      if (!revolutUrl.hostname.endsWith('revolut.com')) {
        throw new Error('Invalid OAuth URL');
      }
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRevolutSync = async (connectionId) => {
    try {
      toast.info(connectionId ? `Syncing Revolut ${connectionId}...` : 'Syncing all Revolut accounts...');
      const result = await revolutAPI.sync(connectionId);
      const d = result.data;
      toast.success(`Revolut synced: ${d.accounts_synced} accounts, ${d.transactions_synced} transactions`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleRevolutDisconnect = async (connectionId) => {
    try {
      await revolutAPI.disconnect(connectionId);
      toast.success(`Revolut ${connectionId} disconnected`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handlePennylaneEntityConnect = (entity) => {
    setPennylaneConnectEntity(entity);
    setShowPennylaneConnect(true);
  };

  const handlePennylaneDisconnect = async (entityId) => {
    try {
      await pennylaneAPI.disconnect(entityId);
      toast.success(`Pennylane disconnected for ${entityId}`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleXeroConnect = async () => {
    try {
      const response = await xeroAPI.initiateAuth();
      const xeroUrl = new URL(response.data.auth_url);
      if (!xeroUrl.hostname.endsWith('xero.com')) {
        throw new Error('Invalid OAuth URL');
      }
      window.location.href = response.data.auth_url;
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleXeroDisconnect = async () => {
    try {
      const res = await xeroAPI.disconnect();
      const d = res.data || {};
      if (d.warning) {
        toast.warning(`Xero disconnected locally, but Xero-side revocation was partial (${d.revoked || 0} revoked, ${d.failed || 0} failed). Check the Xero Connections card on Diagnostics.`);
      } else {
        toast.success('Xero disconnected');
      }
      setShowXeroDetails(false);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleXeroSync = async () => {
    try {
      toast.info('Syncing Xero invoices...');
      const result = await xeroAPI.sync();
      const d = result.data;
      if (d.status === 'dispatched') {
        toast.success('Xero invoice sync started (background job). Check Diagnostics for progress.');
      } else if (d.invoices_synced > 0) {
        toast.success(`Synced ${d.invoices_synced} invoices from Xero`);
      } else {
        toast.info('No new invoices found in Xero');
      }
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleUplistingSync = async () => {
    try {
      toast.info('Syncing Uplisting properties and reservations...');
      const result = await uplistingAPI.sync();
      const d = result.data;
      const props = d.properties?.synced || 0;
      const res = d.reservations?.synced || 0;
      toast.success(`Uplisting synced: ${props} properties, ${res} reservations`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleBrosSync = async () => {
    try {
      toast.info('Syncing BR OS properties, bookings, and invoices...');
      const result = await brosAPI.sync();
      const d = result.data;
      const props = d.properties?.synced || 0;
      const bookings = d.bookings?.synced || 0;
      const invoices = d.invoices?.synced || 0;
      toast.success(`BR OS synced: ${props} properties, ${bookings} bookings, ${invoices} invoices`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleSmoobuSync = async () => {
    try {
      toast.info('Syncing Smoobu properties and reservations...');
      const result = await smoobuAPI.sync();
      const d = result.data;
      const props = d.properties?.synced || 0;
      const res = d.reservations?.synced || 0;
      toast.success(`Smoobu synced: ${props} properties, ${res} reservations`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleHostawaySync = async () => {
    try {
      toast.info('Syncing Hostaway listings and reservations...');
      const result = await hostawayAPI.sync();
      const d = result.data;
      const props = d.listings?.synced || d.properties_synced || 0;
      const res = d.reservations?.synced || d.reservations_synced || 0;
      toast.success(`Hostaway synced: ${props} listings, ${res} reservations`);
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleScraperCookieImport = async (cookiesJson) => {
    try {
      const cookies = JSON.parse(cookiesJson);
      toast.info('Importing cookies...');
      await smoobuAPI.importCookies({ account_id: 'cwc_browser', cookies });
      toast.success('Cookies imported successfully');
      setShowCookieImport(false);
      await fetchAllData();
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid JSON — make sure you copied the cookies correctly');
      } else {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleScraperScrape = async () => {
    try {
      toast.info('Starting CWC Smoobu scrape...');
      const result = await smoobuAPI.triggerScrape({ account_id: 'cwc_browser' });
      const d = result.data;
      if (d.status === 'completed') {
        toast.success(`Scrape complete: ${d.reservations_found || 0} reservations found, ${d.upserted || 0} upserted`);
      } else {
        toast.info('Scrape started in background — check back shortly');
      }
      await fetchAllData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleConnect = async (integration) => {
    if (integration.id === 'pennylane') {
      setShowPennylaneDetails(true);
      return;
    }
    if (integration.id === 'xero' && integration.status === 'connected') {
      setShowXeroDetails(true);
      return;
    }
    if (integration.id === 'xero') {
      handleXeroConnect();
      return;
    }
    // Handle Revolut — show details panel for multi-account management
    if (integration.id === 'revolut') {
      setShowRevolutDetails(true);
      return;
    }
    // Handle Wise — API key is server-side, just trigger sync
    if (integration.id === 'wise') {
      handleWiseSync();
      return;
    }
    // Handle Uplisting — API key is server-side, just trigger sync
    if (integration.id === 'uplisting' && integration.status === 'connected') {
      setShowUplistingDetails(true);
      setTimeout(() => uplistingDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      return;
    }
    if (integration.id === 'uplisting') {
      handleUplistingSync();
      return;
    }
    // Handle BR OS — API key is server-side, just trigger sync
    if (integration.id === 'bros') {
      handleBrosSync();
      return;
    }
    // Handle Smoobu — open dashboard if connected, otherwise sync
    if (integration.id === 'smoobu' && integration.status === 'connected') {
      setShowSmoobuDetails(true);
      setTimeout(() => smoobuDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      return;
    }
    if (integration.id === 'smoobu') {
      handleSmoobuSync();
      return;
    }
    // Handle Hostaway
    if (integration.id === 'hostaway' && integration.status === 'connected') {
      setShowHostawayDetails(true);
      setTimeout(() => hostawayDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      return;
    }
    if (integration.id === 'hostaway') {
      handleHostawaySync();
      return;
    }
    // Handle Smoobu CWC browser scraper
    if (integration.id === 'smoobu_scraper') {
      if (integration.status === 'connected') {
        handleScraperScrape();
      } else {
        setShowCookieImport(true);
      }
      return;
    }
    // Handle Google OAuth integrations
    if (integration.authType === 'google_oauth') {
      handleGoogleConnect(integration);
      return;
    }
    // Handle Bridge connect: call backend, open Bridge Connect UI
    // Scope-gated to Banque BCP Particuliers / Pro (BRIDGE_ALLOWED_BANKS).
    if (integration.authType === 'bridge_connect') {
      try {
        const brand = prompt(
          'Which Banque BCP brand?\n1 = Particuliers (personal)\n2 = Pro (business)',
          '1',
        );
        if (!brand) return;
        const bankName = brand.trim() === '2' ? 'Banque BCP Pro' : 'Banque BCP Particuliers';
        const label = prompt('Connection label:', bankName);
        if (!label) return;
        const res = await bridgeAPI.connect(window.location.origin, label, null, bankName);
        const connectUrl = res.data?.connect_url;
        if (connectUrl) {
          window.location.href = connectUrl;
        } else {
          toast.error('Bridge connect returned no URL');
        }
      } catch (err) {
        const d = err.response?.data;
        const reason = typeof d?.detail === 'string' ? d.detail
          : d?.detail?.reason || d?.reason || d?.error || err.message;
        toast.error(`Bridge connect failed: ${reason}`);
      }
      return;
    }
    // Handle Open Banking connect: show institution picker
    if (integration.authType === 'ob_connect') {
      setShowObInstitutionPicker(true);
      return;
    }
    // Handle Enable Banking connect: show EB institution picker
    if (integration.authType === 'eb_connect') {
      setShowEbInstitutionPicker(true);
      return;
    }
    // Handle GoCardless connect: show the credentials + institution picker dialog
    if (integration.authType === 'gc_connect') {
      setShowGcInstitutionPicker(true);
      gocardlessAPI.getCredentialsStatus()
        .then(({ data }) => setGcCredentialsStatus(data))
        .catch(() => setGcCredentialsStatus(null));
      return;
    }
    setSelectedIntegration(integration);
    setDialogType(integration.authType === 'oauth' ? 'oauth' : 'api_key');
  };

  const handleViewDetails = async (integration) => {
    if (integration.id === 'pennylane') {
      setShowPennylaneDetails(true);
    }
    if (integration.id === 'xero') {
      setShowXeroDetails(true);
    }
    if (integration.id === 'revolut') {
      setShowRevolutDetails(true);
    }
    if (integration.id === 'wise') {
      handleWiseSync();
    }
    if (integration.id === 'bridge') {
      try {
        toast.info('Syncing Bridge (BNP) connections...');
        const res = await bridgeAPI.sync();
        toast.success(`Bridge sync: ${res.data?.accounts_synced || 0} accounts, ${res.data?.transactions_synced || 0} transactions`);
        fetchAllData();
      } catch (err) {
        toast.error(`Bridge sync failed: ${err.response?.data?.detail || err.message}`);
      }
    }
    if (integration.id === 'openbanking') {
      setShowObDetails(true);
    }
    if (integration.id === 'enablebanking') {
      setShowEbDetails(true);
    }
    if (integration.id === 'gocardless') {
      setShowGcDetails(true);
    }
    if (integration.id === 'uplisting') {
      setShowUplistingDetails(true);
      setTimeout(() => uplistingDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
    if (integration.id === 'smoobu') {
      setShowSmoobuDetails(true);
      setTimeout(() => smoobuDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  };

  const handleDisconnect = async (integration) => {
    // Handle Google disconnections
    if (integration.authType === 'google_oauth') {
      handleGoogleDisconnect(integration);
      return;
    }
    if (integration.id === 'revolut') {
      setShowRevolutDetails(true);
      return;
    }
    if (integration.id === 'xero') {
      handleXeroDisconnect();
      return;
    }
    if (integration.id === 'smoobu_scraper') {
      // "Disconnect" = re-import cookies
      setShowCookieImport(true);
      return;
    }
    if (integration.id === 'pennylane') {
      setShowPennylaneDetails(true);
      return;
    }
    if (integration.id === 'bridge') {
      // Disconnect all Bridge connections
      try {
        const conns = integration.bridgeStatus?.connections || [];
        for (const conn of conns) {
          if (conn.connected && conn.connection_id) {
            await bridgeAPI.disconnect(conn.connection_id);
          }
        }
        toast.success('Bridge (BNP) disconnected');
        fetchAllData();
      } catch (err) {
        toast.error(`Bridge disconnect failed: ${err.response?.data?.detail || err.message}`);
      }
      return;
    }
    if (integration.id === 'openbanking') {
      try {
        const conns = integration.obStatus?.connections || [];
        for (const conn of conns) {
          if (conn.connected && conn.connection_id) {
            await openbankingAPI.disconnect(conn.connection_id);
          }
        }
        toast.success('Open banking disconnected');
        fetchAllData();
      } catch (err) {
        toast.error(`Open banking disconnect failed: ${err.response?.data?.detail || err.message}`);
      }
      return;
    }
    toast.success(`${integration.name} disconnected`);
  };
  
  // Ambiguous-provider picker (Commit 3): shared by the institution picker's
  // per-institution button and the "Active connections" Reconnect button, so
  // requires_picker handling never drifts between the two call sites. On a
  // 409 with requires_picker=true, the connection never proceeds -- the
  // picker dialog opens instead of a toast, and the operator's pick is
  // persisted (openbankingAPI.setProviderChoice) before retrying.
  const attemptObConnect = async (inst, isReconnect, connectionId) => {
    try {
      const res = isReconnect
        ? await openbankingAPI.reconnect(connectionId, window.location.origin)
        : await openbankingAPI.connect(inst.id, window.location.origin);
      const authUrl = res.data?.auth_url;
      if (authUrl) {
        window.location.href = authUrl;
      } else {
        toast.error('Open banking connect returned no URL');
      }
    } catch (err) {
      const classified = classifyObConnectError(err.response?.data, inst.name);
      if (classified.kind === 'picker') {
        setObProviderPicker({
          institutionId: classified.institutionId || inst.id,
          institutionName: inst.name,
          candidates: classified.candidates,
          isReconnect,
          connectionId,
        });
        return;
      }
      toast.error(classified.message, { duration: 10000 });
    }
  };

  // Shared by every "Change provider" button (CLAUDE.md Commit 5b) so the
  // clear-then-reconnect sequence can't drift between call sites. Confirms
  // first: this disconnects the existing link and requires re-linking.
  const handleObChangeProvider = async (conn) => {
    if (!window.confirm(
      `Change provider for "${conn.institution_name}"? This clears the saved provider choice and disconnects the current link -- you will need to reconnect and pick the correct provider.`,
    )) {
      return;
    }
    try {
      await openbankingAPI.clearProviderChoice(conn.institution_id);
      await attemptObConnect(
        { id: conn.institution_id, name: conn.institution_name },
        true,
        conn.connection_id,
      );
    } catch (err) {
      toast.error(`Failed to clear provider choice: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleObSync = async () => {
    setObSyncing(true);
    try {
      const res = await openbankingAPI.sync();
      toast.success(`Open banking sync: ${res.data?.accounts_synced || 0} accounts, ${res.data?.transactions_synced || 0} transactions`);
      fetchAllData();
    } catch (err) {
      toast.error(`Open banking sync failed: ${err.response?.data?.detail || err.message}`);
    } finally {
      setObSyncing(false);
    }
  };

  // Idempotent: repoints a reconsent's fresh, unassigned bank_accounts docs
  // onto the operator's existing entity-mapped ones by stable identity
  // (IBAN first). Safe to click repeatedly -- see services/eb_account_relink.py.
  const handleEbRelink = async (connectionId) => {
    setEbRelinkingId(connectionId);
    try {
      const res = await enablebankingAPI.relink(connectionId);
      const d = res.data;
      if (d.relinked_count > 0) {
        toast.success(
          `Relinked ${d.relinked_count} account(s)` +
          (d.ambiguous_count ? `. ${d.ambiguous_count} ambiguous account(s) need manual review.` : '.')
        );
      } else if (d.ambiguous_count > 0) {
        toast.warning(`${d.ambiguous_count} account(s) could not be confidently relinked and need manual review.`);
      } else if (d.pennylane_owned_count > 0) {
        toast.success(`Nothing to relink. ${d.pennylane_owned_count} account(s) are fed by Pennylane, so their transactions come from there.`);
      } else {
        toast.success('Nothing to relink, accounts are already up to date.');
      }
      fetchAllData();
    } catch (err) {
      toast.error(`Relink failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setEbRelinkingId(null);
    }
  };

  // EB only guarantees the accounts array at session creation. A session
  // that landed with consent active but zero accounts (needs_attention on
  // the status card) may just have hit a transient fetch failure -- retry
  // re-fetches via GET /sessions/{id} and relinks any resulting accounts.
  const handleEbRetryFetch = async (connectionId) => {
    setEbRetryingId(connectionId);
    try {
      const res = await enablebankingAPI.retryFetch(connectionId);
      const d = res.data;
      if (d.outcome === 'ok') {
        toast.success(`Retry found ${d.accounts_found} account(s)` + (d.relinked ? `, relinked ${d.relinked}.` : '.'));
      } else if (d.outcome === 'empty') {
        toast.warning('Enable Banking confirmed zero accounts for this session.');
      } else if (d.outcome === 'unknown_shape') {
        toast.warning('Unexpected response from Enable Banking -- needs investigation, not treated as confirmed-empty.');
      } else {
        toast.error('Retry-fetch failed.');
      }
      fetchAllData();
    } catch (err) {
      toast.error(`Retry-fetch failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setEbRetryingId(null);
    }
  };

  // A provider choice is persisted the moment the ambiguous-match picker is
  // answered (setProviderChoice), NOT on connect completion -- so an
  // institution can already be pinned to the wrong provider (e.g. RBS ->
  // Bankline instead of RBS Digital Banking) even though no connection has
  // ever succeeded, meaning there is no connected row for "Change provider"
  // (handleObChangeProvider) to appear on. This is the pre-connect
  // counterpart: reset the pin from the institution picker itself, before
  // the operator has connected at all.
  const handleObResetProviderChoice = async (inst) => {
    if (!window.confirm(
      `Reset the saved provider choice for "${inst.name}"? The next "Connect" click will re-resolve from TrueLayer's live provider list instead of silently reusing the previous pick.`,
    )) {
      return;
    }
    try {
      await openbankingAPI.clearProviderChoice(inst.id);
      const res = await openbankingAPI.getInstitutions();
      setObInstitutions(res.data?.institutions || []);
      toast.success(`Provider choice cleared for ${inst.name} -- click Connect to pick again.`);
    } catch (err) {
      toast.error(`Failed to reset provider choice: ${err.response?.data?.detail || err.message}`);
    }
  };

  const handleObProviderPick = async (providerId) => {
    if (!obProviderPicker) return;
    setObProviderPickerBusy(true);
    try {
      await openbankingAPI.setProviderChoice(obProviderPicker.institutionId, providerId);
      const inst = { id: obProviderPicker.institutionId, name: obProviderPicker.institutionName };
      const { isReconnect, connectionId } = obProviderPicker;
      setObProviderPicker(null);
      await attemptObConnect(inst, isReconnect, connectionId);
    } catch (err) {
      toast.error(`Failed to save provider choice: ${err.response?.data?.detail || err.message}`);
    } finally {
      setObProviderPickerBusy(false);
    }
  };

  const handleConfigure = (integration) => {
    if (integration.id === 'uplisting') {
      setShowUplistingDetails(true);
      setTimeout(() => uplistingDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      return;
    }
    if (integration.id === 'smoobu') {
      setShowSmoobuDetails(true);
      setTimeout(() => smoobuDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      return;
    }
    if (integration.id === 'hostaway') {
      setShowHostawayDetails(true);
      setTimeout(() => hostawayDashboardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      return;
    }
    if (integration.id === 'revolut') {
      setShowRevolutDetails(true);
      return;
    }
    if (integration.id === 'xero') {
      setShowXeroCredentials(true);
      return;
    }
    if (integration.id === 'openbanking') {
      setShowObInstitutionPicker(true);
      return;
    }
    if (integration.id === 'enablebanking') {
      setShowEbInstitutionPicker(true);
      return;
    }
    toast.info(`Configuration for ${integration.name} not yet available`);
  };
  
  const handleDialogClose = (success) => {
    setSelectedIntegration(null);
    setDialogType(null);
  };
  
  // Count connected integrations
  const connectedCount = Object.values(integrations).reduce(
    (acc, cat) => acc + (cat.integrations?.filter((int) => int.status === 'connected').length || 0),
    0
  );
  const totalCount = Object.values(integrations).reduce(
    (acc, cat) => acc + (cat.integrations?.length || 0),
    0
  );
  
  // Get all integrations for "All" tab
  const allIntegrations = Object.values(integrations).flatMap((cat) => cat.integrations || []);
  
  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="integrations-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Integrations</h1>
            <p className="text-muted-foreground mt-1">
              Connect your banking, accounting, and other services
            </p>
          </div>
          <div className="flex items-center gap-3">
            {googleAccounts.some(a => a.connected) && (
              <Button
                onClick={handleScanAllInvoices}
                disabled={isScanningAll}
                data-testid="scan-all-invoices"
              >
                {isScanningAll ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning All...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Scan All Accounts
                  </>
                )}
              </Button>
            )}
            <Badge variant="outline" className="text-sm py-1 px-3">
              {connectedCount}/{totalCount} Connected
            </Badge>
          </div>
        </div>
        
        {/* Bank consents (PSD2 re-consent) -- derived state + one action per connection */}
        <BankConsentsPanel />

        {/* Pennylane Multi-Entity Panel */}
        {showPennylaneDetails && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Pennylane Entities</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowPennylaneDetails(false)}>
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </div>
            <div className="grid gap-3">
              {(pennylaneEntityStatus?.entities || []).map((ent) => (
                <Card key={ent.entity_id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${ent.connected ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <div>
                        <p className="font-medium">{ent.canonical_name}</p>
                        <p className="text-xs text-muted-foreground">
                          {ent.connected
                            ? ent.company_name || 'Connected'
                            : 'Not connected'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {ent.connected ? (
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handlePennylaneDisconnect(ent.entity_id)}
                          data-testid={`pennylane-disconnect-${ent.entity_id}`}
                        >
                          Disconnect
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handlePennylaneEntityConnect(ent)}
                          data-testid={`pennylane-connect-${ent.entity_id}`}
                        >
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
              {(!pennylaneEntityStatus?.entities || pennylaneEntityStatus.entities.length === 0) && (
                <p className="text-sm text-muted-foreground">No Pennylane entities configured in ENTITY_DEFS.</p>
              )}
            </div>

            {/* Existing Pennylane Dashboard (sync controls) for connected entities */}
            {pennylaneStatus?.connected && (
              <div className="mt-6">
                <PennylaneDashboard
                  status={pennylaneStatus}
                  onSync={handleSync}
                  syncHistory={pennylaneSyncHistory}
                  invoices={pennylaneInvoices}
                  schedulerStatus={schedulerStatus}
                  schedulerConfig={schedulerConfig}
                  onUpdateScheduler={handleUpdateScheduler}
                />
              </div>
            )}
          </div>
        )}
        
        {/* Open Banking Details Panel */}
        {showObDetails && obStatus && (
          <ObConnectionsPanel
            obStatus={obStatus}
            obSyncing={obSyncing}
            onSync={handleObSync}
            onClose={() => setShowObDetails(false)}
            onChangeProvider={handleObChangeProvider}
          />
        )}

        {/* Enable Banking Details Panel */}
        {showEbDetails && ebStatus && (
          <div className="mb-6" ref={ebSectionRef}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Enable Banking (EU PSD2) Sessions</h2>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={ebSyncing}
                  onClick={async () => {
                    setEbSyncing(true);
                    setEbSyncProgress(null);
                    try {
                      const res = await enablebankingAPI.sync();
                      const runId = res.data?.run_id;
                      if (!runId) {
                        // No run started (e.g. no sessions)
                        toast.success(res.data?.message || 'No EB sessions to sync');
                        setEbSyncing(false);
                        return;
                      }
                      if (res.data?.already_running) {
                        toast.info('EB sync already running');
                      }
                      // Poll for progress
                      const poll = async () => {
                        for (let i = 0; i < 120; i++) {
                          await new Promise(r => setTimeout(r, 2000));
                          try {
                            const status = await enablebankingAPI.getSyncRunStatus(runId);
                            const d = status.data;
                            setEbSyncProgress(d);
                            if (d.status === 'complete') {
                              const ok = d.synced || 0;
                              const fail = d.failed || 0;
                              if (fail > 0) {
                                toast.warning(`EB sync done: ${ok} synced, ${fail} failed`);
                              } else {
                                toast.success(`EB sync complete: ${ok} accounts synced`);
                              }
                              fetchAllData();
                              return;
                            }
                          } catch {
                            // transient poll error, retry
                          }
                        }
                        toast.warning('EB sync polling timed out (job may still be running)');
                      };
                      await poll();
                    } catch (err) {
                      toast.error(`Enable Banking sync failed: ${err.response?.data?.detail || err.message}`);
                    } finally {
                      setEbSyncing(false);
                    }
                  }}
                >
                  {ebSyncing
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {ebSyncProgress ? `${(ebSyncProgress.synced || 0) + (ebSyncProgress.failed || 0)}/${ebSyncProgress.total_accounts || '?'}` : 'Starting...'}</>
                    : <><RefreshCw className="w-3 h-3 mr-1" /> Sync Balances</>}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={ebTxSyncing}
                  onClick={async () => {
                    setEbTxSyncing(true);
                    setEbTxSyncProgress(null);
                    try {
                      const res = await enablebankingAPI.txSync();
                      const runId = res.data?.run_id;
                      if (!runId) {
                        toast.success(res.data?.message || 'No EB sessions to sync');
                        setEbTxSyncing(false);
                        return;
                      }
                      if (res.data?.already_running) {
                        toast.info('EB transaction sync already running');
                      }
                      const poll = async () => {
                        for (let i = 0; i < 120; i++) {
                          await new Promise(r => setTimeout(r, 2000));
                          try {
                            const status = await enablebankingAPI.getTxSyncRunStatus(runId);
                            const d = status.data;
                            setEbTxSyncProgress(d);
                            if (d.status === 'complete') {
                              const ok = d.synced || 0;
                              const fail = d.failed || 0;
                              if (fail > 0) {
                                toast.warning(`EB tx sync done: ${ok} synced, ${fail} failed`);
                              } else {
                                toast.success(`EB transaction sync complete: ${ok} accounts synced`);
                              }
                              fetchAllData();
                              return;
                            }
                          } catch {
                            // transient poll error, retry
                          }
                        }
                        toast.warning('EB tx sync polling timed out (job may still be running)');
                      };
                      await poll();
                    } catch (err) {
                      toast.error(`EB transaction sync failed: ${err.response?.data?.detail || err.message}`);
                    } finally {
                      setEbTxSyncing(false);
                    }
                  }}
                >
                  {ebTxSyncing
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> {ebTxSyncProgress ? `${(ebTxSyncProgress.synced || 0) + (ebTxSyncProgress.failed || 0)}/${ebTxSyncProgress.total_accounts || '?'}` : 'Starting...'}</>
                    : <><Download className="w-3 h-3 mr-1" /> Sync Transactions</>}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowEbDetails(false)}>
                  <X className="w-4 h-4 mr-1" /> Close
                </Button>
              </div>
            </div>
            <div className="grid gap-3">
              {(ebStatus.sessions || []).map((session) => {
                const needsAttention = !!session.needs_attention;
                const fetchOutcome = session.account_fetch_outcome;
                return (
                  <Card key={session.connection_id} className={`p-4 ${needsAttention ? 'border-amber-400 dark:border-amber-600' : ''}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${needsAttention ? 'bg-amber-500' : session.connected ? 'bg-green-500' : 'bg-gray-300'}`} />
                        <div className="min-w-0">
                          <p className="font-medium">{session.institution_name || session.label || session.connection_id}</p>
                          <p className={`text-xs ${needsAttention ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}>
                            {session.account_count || 0} account(s)
                            {session.psu_type && ` \u00B7 ${session.psu_type}`}
                            {/* Derived state (backend consent_state), not the connect-time stamp. */}
                            {session.consent_state === 'healthy' && !needsAttention && ' \u00B7 consent active'}
                            {session.consent_state === 'expiring_soon' && ` \u00B7 consent expires in ${Math.round(session.days_until_expiry)}d`}
                            {(session.consent_state === 'expired' || session.consent_state === 'reconsent_required' || session.consent_status === 'reconsent_required') && ' \u00B7 reconsent required'}
                            {session.consent_state === 'no_data' && ' \u00B7 no data arriving \u2014 reconsent'}
                            {needsAttention && fetchOutcome === 'empty' && ' \u00B7 account fetch returned empty'}
                            {needsAttention && fetchOutcome === 'unknown_shape' && ' \u00B7 account fetch: unexpected response'}
                            {needsAttention && fetchOutcome === 'retry_failed' && ' \u00B7 account fetch failed'}
                            {needsAttention && !fetchOutcome && ' \u00B7 account fetch failed/empty'}
                            {session.account_fetch_at && (
                              <> at {new Date(session.account_fetch_at).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</>
                            )}
                          </p>
                          {session.connected_at && (
                            <p className="text-xs text-muted-foreground">
                              Connected: {new Date(session.connected_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 flex-shrink-0">
                        {needsAttention && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={ebRetryingId === session.connection_id}
                            onClick={() => handleEbRetryFetch(session.connection_id)}
                          >
                            {ebRetryingId === session.connection_id
                              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              : <RefreshCw className="w-3 h-3 mr-1" />}
                            Retry
                          </Button>
                        )}
                        {session.connected && (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={ebRelinkingId === session.connection_id}
                            title="Repoint any accounts orphaned by a reconsent back onto their entity mapping, by IBAN."
                            onClick={() => handleEbRelink(session.connection_id)}
                          >
                            {ebRelinkingId === session.connection_id
                              ? <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                              : <RefreshCw className="w-3 h-3 mr-1" />}
                            Relink accounts
                          </Button>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
              {(!ebStatus.sessions || ebStatus.sessions.length === 0) && (
                <p className="text-sm text-muted-foreground">No Enable Banking sessions. Click Configure to connect a bank.</p>
              )}
            </div>

            {/* Per-account sync outcome summary */}
            {ebSyncProgress?.status === 'complete' && ebSyncProgress.account_outcomes?.length > 0 && (
              <div className="mt-4 border rounded-lg p-3">
                <h3 className="text-sm font-semibold mb-2">Balance Sync Results</h3>
                <div className="space-y-1">
                  {ebSyncProgress.account_outcomes.map((o, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${o.status === 'ok' ? 'bg-green-500' : 'bg-red-400'}`} />
                        <span className="text-muted-foreground">{o.account_name || o.account_id}</span>
                      </div>
                      <span className="font-mono text-xs">
                        {o.status === 'ok'
                          ? `${o.currency || ''} ${o.balance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${o.balance_type_used ? ` [${o.balance_type_used}]` : ''}`
                          : o.status === 'no_supported_type' && o.received_types?.length
                            ? `no_supported_type: [${o.received_types.join(', ')}]`
                            : `${o.currency ? o.currency + ' ' : ''}${o.status}${o.error_detail ? ': ' + o.error_detail : ''}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Per-account transaction sync outcome summary */}
            {ebTxSyncProgress?.status === 'complete' && ebTxSyncProgress.account_outcomes?.length > 0 && (
              <div className="mt-4 border rounded-lg p-3">
                <h3 className="text-sm font-semibold mb-2">Transaction Sync Results</h3>
                <div className="space-y-1">
                  {ebTxSyncProgress.account_outcomes.map((o, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${o.status === 'ok' ? 'bg-green-500' : 'bg-red-400'}`} />
                        <span className="text-muted-foreground">{o.account_name || o.account_id}</span>
                      </div>
                      <span className="font-mono text-xs">
                        {o.status === 'ok'
                          ? `${o.inserted || 0} new, ${o.duplicates || 0} dup, ${o.fetched || 0} fetched`
                          : `${o.status}${o.error_detail ? ': ' + o.error_detail : ''}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* GoCardless Bank Account Data connections */}
        {showGcDetails && gcStatus && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">GoCardless Connections</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowGcDetails(false)}>
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </div>
            <div className="grid gap-3">
              {(gcStatus.connections || []).map((conn) => {
                const expiringSoon = conn.consent_status === 'expiring_soon';
                const expired = conn.consent_status === 'expired' || conn.status === 'expired';
                const dotColor = conn.status === 'linked' ? (expiringSoon ? 'bg-amber-500' : 'bg-green-500') : 'bg-gray-300';
                return (
                  <Card key={conn.connection_id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-2.5 h-2.5 rounded-full ${dotColor}`} />
                        <div>
                          <p className="font-medium">{conn.institution_name || conn.label || conn.connection_id}</p>
                          <p className="text-xs text-muted-foreground">
                            {conn.account_count || 0} account(s) · status: {conn.status}
                            {conn.last_synced && ` · last synced ${new Date(conn.last_synced).toLocaleString('en-GB')}`}
                          </p>
                          {conn.expires_in_days !== null && conn.expires_in_days !== undefined && (
                            <p className={`text-xs ${expired ? 'text-red-600 font-medium' : expiringSoon ? 'text-amber-600 font-medium' : 'text-muted-foreground'}`}>
                              {expired
                                ? 'Consent expired'
                                : `Consent expires in ${conn.expires_in_days} day(s)`}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        {(expired || expiringSoon) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                const res = await gocardlessAPI.reconnect(conn.connection_id, window.location.origin);
                                const authUrl = res.data?.auth_url;
                                if (authUrl) window.location.href = authUrl;
                              } catch (err) {
                                toast.error(`Reconnect failed: ${err.response?.data?.detail || err.message}`);
                              }
                            }}
                          >
                            Reconnect
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={gcSyncingId === conn.connection_id}
                          onClick={async () => {
                            setGcSyncingId(conn.connection_id);
                            setGcSyncProgress(null);
                            try {
                              const res = await gocardlessAPI.sync(conn.connection_id);
                              const runId = res.data?.run_id;
                              if (!runId) {
                                toast.success(res.data?.message || 'Nothing to sync');
                                setGcSyncingId(null);
                                return;
                              }
                              if (res.data?.already_running) {
                                toast.info('GoCardless sync already running');
                              }
                              for (let i = 0; i < 120; i++) {
                                await new Promise(r => setTimeout(r, 2000));
                                try {
                                  const status = await gocardlessAPI.getSyncRunStatus(runId);
                                  const d = status.data;
                                  setGcSyncProgress(d);
                                  if (d.status === 'complete') {
                                    const outcomes = d.account_outcomes || [];
                                    const ok = outcomes.filter(o => o.status === 'ok').length;
                                    const failed = outcomes.length - ok;
                                    if (failed > 0) {
                                      toast.warning(`GoCardless sync done: ${ok} ok, ${failed} failed/skipped`);
                                    } else {
                                      toast.success(`GoCardless sync complete: ${ok} account(s) synced`);
                                    }
                                    fetchAllData();
                                    break;
                                  }
                                } catch {
                                  // transient poll error, retry
                                }
                              }
                            } catch (err) {
                              toast.error(`GoCardless sync failed: ${err.response?.data?.detail || err.message}`);
                            } finally {
                              setGcSyncingId(null);
                            }
                          }}
                        >
                          {gcSyncingId === conn.connection_id
                            ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Syncing...</>
                            : <><RefreshCw className="w-3 h-3 mr-1" /> Sync now</>}
                        </Button>
                      </div>
                    </div>
                  </Card>
                );
              })}
              {(!gcStatus.connections || gcStatus.connections.length === 0) && (
                <p className="text-sm text-muted-foreground">No GoCardless connections. Click Configure to connect a bank.</p>
              )}
            </div>

            {gcSyncProgress?.status === 'complete' && gcSyncProgress.account_outcomes?.length > 0 && (
              <div className="mt-4 border rounded-lg p-3">
                <h3 className="text-sm font-semibold mb-2">Sync Results</h3>
                <div className="space-y-1">
                  {gcSyncProgress.account_outcomes.map((o, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full ${o.status === 'ok' ? 'bg-green-500' : 'bg-red-400'}`} />
                        <span className="text-muted-foreground">{o.account_id}</span>
                      </div>
                      <span className="font-mono text-xs">
                        {o.status === 'ok'
                          ? `${o.inserted || 0} new, ${o.duplicates || 0} dup, ${o.fetched || 0} fetched`
                          : `${o.status}${o.endpoint ? ` (${o.endpoint})` : ''}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Revolut Multi-Account Panel */}
        {showRevolutDetails && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Revolut Business Accounts</h2>
              <div className="flex gap-2">
                {revolutStatus?.connected && (
                  <Button variant="outline" size="sm" onClick={() => handleRevolutSync()}>
                    Sync All
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setShowRevolutDetails(false)}>
                  <X className="w-4 h-4 mr-1" /> Close
                </Button>
              </div>
            </div>
            <div className="grid gap-3">
              {(revolutStatus?.accounts || []).map((acc) => (
                <Card key={acc.id} className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full ${acc.connected ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <div>
                        <p className="font-medium">{acc.label}</p>
                        <p className="text-xs text-muted-foreground">
                          {acc.connected
                            ? `${acc.accounts_count || 0} account(s) synced`
                            : 'Not connected'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      {acc.connected ? (
                        <>
                          <Button variant="outline" size="sm" onClick={() => handleRevolutSync(acc.id)}>
                            Sync
                          </Button>
                          {acc.payments_enabled ? (
                            <span className="text-xs text-muted-foreground self-center" data-testid={`revolut-payments-on-${acc.id}`}>Payments on</span>
                          ) : (
                            <Button variant="outline" size="sm"
                              title="Reconnect with permission to make the scheduled payments. Allow the server IP on the Revolut API certificate first."
                              onClick={() => handleRevolutConnect(acc.id, { payments: true })}>
                              Enable payments
                            </Button>
                          )}
                          <Button variant="destructive" size="sm" onClick={() => handleRevolutDisconnect(acc.id)}>
                            Disconnect
                          </Button>
                        </>
                      ) : (
                        <Button size="sm" onClick={() => handleRevolutConnect(acc.id)}>
                          Connect
                        </Button>
                      )}
                    </div>
                  </div>
                  {acc.connected && acc.balances?.length > 0 && (
                    <div className="mt-3 pl-6 space-y-1">
                      {acc.balances.map((b, i) => (
                        <div key={i} className="flex justify-between text-sm text-muted-foreground">
                          <span>{b.name || b.currency}</span>
                          <span className="font-mono">
                            {b.currency === 'GBP' ? '£' : b.currency === 'EUR' ? '€' : b.currency === 'USD' ? '$' : b.currency}{' '}
                            {b.balance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
              {(!revolutStatus?.accounts || revolutStatus.accounts.length === 0) && (
                <p className="text-sm text-muted-foreground">No Revolut accounts configured. Set REVOLUT_*_CLIENT_ID environment variables.</p>
              )}
            </div>
            {revolutStatus?.connected && <RevolutScheduledPayments />}
          </div>
        )}

        {/* Xero Dashboard (if connected) */}
        {xeroStatus?.connected && showXeroDetails && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Xero Dashboard</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowXeroDetails(false)}>
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </div>
            <XeroDashboard
              status={xeroStatus}
              onSync={handleXeroSync}
              onDisconnect={handleXeroDisconnect}
              onReconnect={handleXeroConnect}
            />
          </div>
        )}

        {/* Uplisting Dashboard */}
        {showUplistingDetails && (
          <div className="mb-6" ref={uplistingDashboardRef}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Uplisting Dashboard</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowUplistingDetails(false)}>
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </div>
            <UplistingDashboard
              status={uplistingStatus}
              onSync={handleUplistingSync}
              onDisconnect={() => toast.info('Contact support to disconnect Uplisting')}
            />
          </div>
        )}

        {/* Smoobu Dashboard */}
        {showSmoobuDetails && (
          <div className="mb-6" ref={smoobuDashboardRef}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Smoobu Dashboard</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowSmoobuDetails(false)}>
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </div>
            <SmoobuDashboard
              status={smoobuStatus}
              onSync={handleSmoobuSync}
            />
          </div>
        )}

        {showHostawayDetails && (
          <div className="mb-6" ref={hostawayDashboardRef}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Hostaway Dashboard</h2>
              <Button variant="ghost" size="sm" onClick={() => setShowHostawayDetails(false)}>
                <X className="w-4 h-4 mr-1" /> Close
              </Button>
            </div>
            <HostawayDashboard
              status={hostawayStatus}
              onSync={handleHostawaySync}
            />
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(integrations).map(([key, category]) => {
            const Icon = category.icon;
            const connected = category.integrations?.filter((int) => int.status === 'connected').length || 0;
            const total = category.integrations?.length || 0;
            
            return (
              <Card 
                key={key}
                className="cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => setActiveTab(key)}
                data-testid={`category-${key}`}
              >
                <CardContent className="p-4 text-center">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mx-auto mb-2">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <p className="font-medium text-sm">{category.label}</p>
                  <p className="text-xs text-muted-foreground">{connected}/{total} connected</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
        
        {/* Integrations Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="all">All</TabsTrigger>
            {Object.entries(integrations).map(([key, category]) => (
              <TabsTrigger key={key} value={key}>{category.label}</TabsTrigger>
            ))}
            <TabsTrigger value="api_keys" className="gap-1.5">
              <Key className="w-3.5 h-3.5" />
              API Keys
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="all">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {allIntegrations.map((integration) => (
                integration.authType === 'google_oauth' ? (
                  <GoogleAccountCard
                    key={integration.id}
                    account={integration}
                    onConnect={handleGoogleConnect}
                    onDisconnect={handleGoogleDisconnect}
                    onScanInvoices={handleScanInvoices}
                  />
                ) : (
                  <IntegrationCard
                    key={integration.id}
                    integration={integration}
                    onConnect={handleConnect}
                    onDisconnect={handleDisconnect}
                    onConfigure={handleConfigure}
                    onViewDetails={handleViewDetails}
                  />
                )
              ))}
            </div>
          </TabsContent>
          
          {Object.entries(integrations).map(([key, category]) => (
            <TabsContent key={key} value={key}>
              <Card className="mb-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <category.icon className="w-5 h-5" />
                    {category.label}
                  </CardTitle>
                  <CardDescription>{category.description}</CardDescription>
                </CardHeader>
              </Card>
              {key === 'email' && <DriveDestinationSettingsCard />}
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {category.integrations?.map((integration) => (
                  integration.authType === 'google_oauth' ? (
                    <GoogleAccountCard
                      key={integration.id}
                      account={integration}
                      onConnect={handleGoogleConnect}
                      onDisconnect={handleGoogleDisconnect}
                      onScanInvoices={handleScanInvoices}
                    />
                  ) : (
                    <IntegrationCard
                      key={integration.id}
                      integration={integration}
                      onConnect={handleConnect}
                      onDisconnect={handleDisconnect}
                      onConfigure={handleConfigure}
                      onViewDetails={handleViewDetails}
                    />
                  )
                ))}
              </div>
            </TabsContent>
          ))}

          <TabsContent value="api_keys">
            <APIKeysTab />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Connection Dialogs */}
      <OAuthConnectDialog
        integration={selectedIntegration}
        open={dialogType === 'oauth'}
        onClose={handleDialogClose}
      />
      <ApiKeyConnectDialog
        integration={selectedIntegration}
        open={dialogType === 'api_key'}
        onClose={handleDialogClose}
      />
      <PennylaneConnectDialog
        entity={pennylaneConnectEntity}
        open={showPennylaneConnect}
        onClose={() => { setShowPennylaneConnect(false); setPennylaneConnectEntity(null); }}
        onSuccess={() => fetchAllData()}
      />
      <XeroCredentialsDialog
        open={showXeroCredentials}
        onClose={() => setShowXeroCredentials(false)}
        onSuccess={() => fetchAllData()}
      />
      <CookieImportDialog
        open={showCookieImport}
        onClose={() => setShowCookieImport(false)}
        onImport={handleScraperCookieImport}
        scraperStatus={scraperStatus}
      />

      {/* Open Banking institution picker */}
      <Dialog open={showObInstitutionPicker} onOpenChange={setShowObInstitutionPicker}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect bank account (Open Banking)</DialogTitle>
            <DialogDescription>
              {obStatus?.is_sandbox
                ? 'Sandbox environment. Connect to Mock Bank for testing (username: john, password: doe).'
                : 'Select the bank to connect via TrueLayer. You will be redirected to complete Strong Customer Authentication.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {obInstitutions.map((inst) => {
              // Find existing connection for this institution
              const existingConn = obStatus?.connections?.find(
                (c) => c.institution_id === inst.id && c.connected,
              );
              const isReconnect = !!existingConn;
              return (
                <div key={inst.id}>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left h-auto py-3"
                    onClick={async () => {
                      setShowObInstitutionPicker(false);
                      await attemptObConnect(inst, isReconnect, isReconnect ? existingConn.connection_id : undefined);
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <div className="font-medium">{inst.name}</div>
                        <div className="text-xs text-muted-foreground">{inst.country}</div>
                      </div>
                      {isReconnect && (
                        <Badge variant="outline" className="text-xs ml-2">Reconnect</Badge>
                      )}
                    </div>
                  </Button>
                  {inst.has_provider_choice && (
                    <div className="flex items-center justify-between px-1 pt-1">
                      <span className="text-xs text-amber-600">
                        A provider is already saved for this bank -- "Connect" will reuse it silently.
                      </span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-xs h-auto py-0.5"
                        data-testid={`ob-reset-provider-choice-${inst.id}`}
                        onClick={() => handleObResetProviderChoice(inst)}
                      >
                        Reset provider choice
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {obStatus?.connections?.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-2">Active connections</h4>
              {obStatus.connections.filter((c) => c.connected).map((conn) => (
                <div key={conn.connection_id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <span className="font-medium">{conn.institution_name}</span>
                    <span className="text-muted-foreground ml-2">
                      {conn.account_count || 0} account(s)
                    </span>
                    {conn.consent_status === 'expired' && (
                      <Badge variant="destructive" className="ml-2 text-xs">Consent expired</Badge>
                    )}
                    {conn.consent_status === 'expiring_soon' && (
                      <Badge variant="warning" className="ml-2 text-xs">Expiring soon</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        await attemptObConnect(
                          { id: conn.institution_id, name: conn.institution_name },
                          true,
                          conn.connection_id,
                        );
                      }}
                    >
                      Reconnect
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={async () => {
                        try {
                          toast.info('Syncing...');
                          const res = await openbankingAPI.sync(conn.connection_id);
                          toast.success(`Sync: ${res.data?.accounts_synced || 0} accounts, ${res.data?.transactions_synced || 0} transactions`);
                          fetchAllData();
                        } catch (err) {
                          toast.error(`Sync failed: ${err.response?.data?.detail || err.message}`);
                        }
                      }}
                    >
                      Sync now
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      data-testid={`ob-picker-change-provider-${conn.connection_id}`}
                      onClick={() => handleObChangeProvider(conn)}
                    >
                      Change provider
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowObInstitutionPicker(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ambiguous TrueLayer provider picker (Commit 3): surfaced when
          connect/reconnect returns requires_picker=true -- an ambiguous
          provider_match pattern hit more than one live provider and cannot
          proceed until the operator picks one. Never auto-picks; the choice
          is persisted so this never recurs for the same institution. */}
      <Dialog open={!!obProviderPicker} onOpenChange={(o) => { if (!o && !obProviderPickerBusy) setObProviderPicker(null); }}>
        <DialogContent data-testid="ob-provider-picker-dialog">
          <DialogHeader>
            <DialogTitle>Which {obProviderPicker?.institutionName} account?</DialogTitle>
            <DialogDescription>
              TrueLayer lists more than one provider matching "{obProviderPicker?.institutionName}".
              Pick the correct one -- this choice is saved so you won't be asked again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {(obProviderPicker?.candidates || []).map((c) => (
              <Button
                key={c.provider_id}
                variant="outline"
                className="w-full justify-start text-left h-auto py-3"
                disabled={obProviderPickerBusy}
                onClick={() => handleObProviderPick(c.provider_id)}
                data-testid={`ob-provider-candidate-${c.provider_id}`}
              >
                <div className="flex items-center justify-between w-full">
                  <div>
                    <div className="font-medium">{c.display_name || c.provider_id}</div>
                    <div className="text-xs text-muted-foreground font-mono">{c.provider_id} &middot; {c.country}</div>
                  </div>
                  {obProviderPickerBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={obProviderPickerBusy} onClick={() => setObProviderPicker(null)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enable Banking institution picker */}
      <Dialog open={showEbInstitutionPicker} onOpenChange={setShowEbInstitutionPicker}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect bank account (Enable Banking)</DialogTitle>
            <DialogDescription>
              Select the bank to connect via PSD2 (Enable Banking). You will be redirected to complete consent authorization.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {ebInstitutions.map((inst) => {
              const existingSession = ebStatus?.sessions?.find(
                (s) => s.institution_id === inst.id && s.connected,
              );
              const isReconnect = !!existingSession;
              const isDisabled = !inst.enabled;
              return (
                <div key={inst.id} className="space-y-2">
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left h-auto py-3"
                    disabled={isDisabled}
                    onClick={async () => {
                      // For institutions with psu_type options (BNP), use the selected type
                      const psuSelect = document.getElementById(`psu-${inst.id}`);
                      const psuType = psuSelect?.value || 'personal';
                      setShowEbInstitutionPicker(false);
                      try {
                        const res = await enablebankingAPI.connect(
                          inst.id, psuType, window.location.origin,
                        );
                        const authUrl = res.data?.auth_url;
                        if (authUrl) {
                          window.location.href = authUrl;
                        } else {
                          toast.error('Enable Banking connect returned no URL');
                        }
                      } catch (err) {
                        const errData = err.response?.data;
                        if (errData?.step && errData?.error) {
                          toast.error(
                            `Enable Banking connect failed (${errData.step}): ${errData.error}`,
                            { duration: 10000 },
                          );
                        } else {
                          toast.error(`Enable Banking connect failed: ${errData?.detail || errData?.error || err.message}`);
                        }
                      }
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <div className="font-medium">
                          {inst.name}
                          {isDisabled && (
                            <span className="text-muted-foreground ml-1 font-normal">(coverage pending)</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">{inst.country}</div>
                      </div>
                      {isReconnect && (
                        <Badge variant="outline" className="text-xs ml-2">Reconnect</Badge>
                      )}
                    </div>
                  </Button>
                  {/* PSU type selector for multi-type institutions (BNP) */}
                  {inst.enabled && (
                    <div className="flex items-center gap-2 pl-4">
                      <label htmlFor={`psu-${inst.id}`} className="text-xs text-muted-foreground">
                        Account type:
                      </label>
                      <select
                        id={`psu-${inst.id}`}
                        defaultValue="personal"
                        className="text-xs border rounded px-2 py-1 bg-background"
                      >
                        <option value="personal">Personal</option>
                        <option value="business">Business</option>
                      </select>
                    </div>
                  )}
                </div>
              );
            })}
            {ebInstitutions.length === 0 && (
              <p className="text-sm text-muted-foreground">No Enable Banking institutions configured.</p>
            )}
          </div>
          {ebStatus?.sessions?.length > 0 && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium mb-2">Active sessions</h4>
              {ebStatus.sessions.filter((s) => s.connected).map((session) => (
                <div key={session.connection_id} className="flex items-center justify-between py-2 text-sm">
                  <div>
                    <span className="font-medium">{session.institution_name}</span>
                    <span className="text-muted-foreground ml-2">
                      {session.account_count || 0} account(s)
                      {session.psu_type && ` \u00B7 ${session.psu_type}`}
                    </span>
                    {session.consent_status === 'reconsent_required' && (
                      <Badge variant="destructive" className="ml-2 text-xs">Reconsent needed</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEbInstitutionPicker(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* GoCardless credentials + institution picker */}
      <Dialog open={showGcInstitutionPicker} onOpenChange={setShowGcInstitutionPicker}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Connect bank account (GoCardless)</DialogTitle>
            <DialogDescription>
              AIS access via GoCardless Bank Account Data. Defaults to Banque BCP but
              covers any EU institution GoCardless supports.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="text-xs text-muted-foreground underline"
                onClick={() => setShowGcCredentialsForm((v) => !v)}
              >
                {showGcCredentialsForm ? 'Hide credentials form' : 'Configure GoCardless API credentials'}
              </button>
              {gcCredentialsStatus && (
                <Badge
                  data-testid="gc-credentials-status-badge"
                  className={gcCredentialsStatus.configured
                    ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 text-[10px]'
                    : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-[10px]'}
                >
                  {gcCredentialsStatus.configured ? 'Credentials configured' : 'Not configured'}
                </Badge>
              )}
            </div>
            {showGcCredentialsForm && (
              <div className="space-y-2 border rounded-lg p-3">
                <p className="text-xs text-muted-foreground">
                  From the GoCardless Bank Account Data portal. Stored encrypted; never shown again.
                </p>
                <Input
                  type="text"
                  placeholder="Secret ID"
                  value={gcSecretId}
                  onChange={(e) => setGcSecretId(e.target.value)}
                />
                <Input
                  type="password"
                  placeholder="Secret Key"
                  value={gcSecretKey}
                  onChange={(e) => setGcSecretKey(e.target.value)}
                />
                <Button
                  size="sm"
                  disabled={gcSavingCredentials || !gcSecretId.trim() || !gcSecretKey.trim()}
                  onClick={async () => {
                    setGcSavingCredentials(true);
                    try {
                      await gocardlessAPI.saveCredentials(gcSecretId.trim(), gcSecretKey.trim());
                      toast.success('GoCardless credentials saved');
                      setGcSecretId('');
                      setGcSecretKey('');
                      setShowGcCredentialsForm(false);
                      gocardlessAPI.getCredentialsStatus()
                        .then(({ data }) => setGcCredentialsStatus(data))
                        .catch(() => {});
                      fetchAllData();
                    } catch (err) {
                      toast.error(`Failed to save credentials: ${err.response?.data?.error || err.message}`);
                    } finally {
                      setGcSavingCredentials(false);
                    }
                  }}
                >
                  {gcSavingCredentials ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
                  Save credentials
                </Button>
              </div>
            )}
          </div>

          <Input
            type="text"
            placeholder="Search institutions..."
            value={gcInstitutionFilter}
            onChange={(e) => setGcInstitutionFilter(e.target.value)}
            className="mb-2"
          />

          <div className="space-y-2 py-2 max-h-80 overflow-y-auto">
            {(() => {
              const filterText = gcInstitutionFilter.trim().toLowerCase();
              const list = gcInstitutions.length > 0
                ? gcInstitutions
                : [{ id: GC_DEFAULT_INSTITUTION_ID, name: 'Banque BCP', bic: 'BPSMFRPPXXX' }];
              const filtered = filterText
                ? list.filter((inst) => (inst.name || '').toLowerCase().includes(filterText))
                : list;
              // Pin Banque BCP to the top when not actively searching.
              const sorted = filterText
                ? filtered
                : [...filtered].sort((a, b) => (
                    (a.id === GC_DEFAULT_INSTITUTION_ID ? -1 : 0) - (b.id === GC_DEFAULT_INSTITUTION_ID ? -1 : 0)
                  ));
              if (sorted.length === 0) {
                return <p className="text-sm text-muted-foreground">No institutions match "{gcInstitutionFilter}".</p>;
              }
              return sorted.map((inst) => {
                const existingConn = gcStatus?.connections?.find(
                  (c) => c.institution_id === inst.id && c.status === 'linked',
                );
                const isReconnect = !!existingConn;
                return (
                  <Button
                    key={inst.id}
                    variant="outline"
                    className="w-full justify-start text-left h-auto py-3"
                    onClick={async () => {
                      setShowGcInstitutionPicker(false);
                      try {
                        let res;
                        if (isReconnect) {
                          res = await gocardlessAPI.reconnect(existingConn.connection_id, window.location.origin);
                        } else {
                          res = await gocardlessAPI.connect(inst.id, window.location.origin);
                        }
                        const authUrl = res.data?.auth_url;
                        if (authUrl) {
                          window.location.href = authUrl;
                        } else {
                          toast.error('GoCardless connect returned no URL');
                        }
                      } catch (err) {
                        const errData = err.response?.data;
                        toast.error(
                          `GoCardless connect failed${errData?.step ? ` (${errData.step})` : ''}: ${errData?.error || errData?.detail || err.message}`,
                          { duration: 10000 },
                        );
                      }
                    }}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div>
                        <div className="font-medium">
                          {inst.name}
                          {inst.id === GC_DEFAULT_INSTITUTION_ID && (
                            <Badge variant="outline" className="text-[10px] ml-2">Default</Badge>
                          )}
                        </div>
                        {inst.bic && <div className="text-xs text-muted-foreground">{inst.bic}</div>}
                      </div>
                      {isReconnect && (
                        <Badge variant="outline" className="text-xs ml-2">Reconnect</Badge>
                      )}
                    </div>
                  </Button>
                );
              });
            })()}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGcInstitutionPicker(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

export default IntegrationsPage;
